import React, { useState } from 'react';
import { Calculator, Target, Wind, Thermometer, Gauge, Download, RotateCcw, Info, HelpCircle, Play, X, ChevronRight, ChevronLeft, Plus, Trash2, Eye, EyeOff } from 'lucide-react';

const BallisticsCalculator = () => {
  const [formData, setFormData] = useState({
    unitSystem: 'imperial',
    dragModel: 'G1',
    gameSize: 'none',
    bulletCompany: '',
    bulletCaliber: '',
    bulletType: '',
    bulletDescription: '',
    bulletWeight: 180,
    elevation: 0,
    temperature: 59,
    pressure: 29.92,
    humidity: 50,
    windSpeed: 10,
    scopeHeight: 1.75,
    zeroRange: 100,
    zeroAdjustment: 0,
    inclination: 0,
    clickValue: 0.25,
    useBC: true,
    ballisticCoefficient: 0.5,
    muzzleVelocity: 2800,
    startRange: 100,
    endRange: 1500,
    rangeIncrement: 50
  });

  // Each point matches bccalculator.com's field data form:
  // Target (yards), Clicks (scope clicks dialed), Adjustment (inches still off POA).
  const [fieldDataPoints, setFieldDataPoints] = useState([
    { id: 'primary', target: '', clicks: '', adjustment: '' }
  ]);

  // Alternate Conditions columns — each optionally adds a variation column to the
  // trajectory output, reflecting either an alternate inclination or alternate
  // atmosphere (elevation/temperature; pressure auto-derived from elevation).
  const [alternateColumns, setAlternateColumns] = useState([
    { type: 'None', incline: 0, elevation: 0, temperature: 59, showVariation: true },
    { type: 'None', incline: 0, elevation: 0, temperature: 59, showVariation: true }
  ]);

  const updateAlternateColumn = (idx, field, value) => {
    setAlternateColumns(cols => cols.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  // ICAO station pressure (inHg) from altitude (ft), using the clone's
  // existing ATMOS lapse-rate constants. Matches legacy auto-derivation.
  const pressureFromAltitude = (altFt) => {
    const A = { P0: 29.92, G: 32.174, B: 0.00357, R: 1716, TR: 518.67 };
    const exp = A.G / (A.R * A.B);
    return A.P0 * Math.pow((A.TR - A.B * altFt) / A.TR, exp);
  };
  const [showFieldData, setShowFieldData] = useState(true);
  const [fieldDataCollapsed, setFieldDataCollapsed] = useState(true);

  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const tutorialSteps = [
    {
      title: "Welcome to the Enhanced Ballistics Calculator",
      content: "This comprehensive ballistics calculator includes an extensive bullet database and field data tracking to help you determine accurate bullet trajectories for long-range shooting."
    },
    {
      title: "Comprehensive Bullet Database",
      content: "Our database includes bullets from all major manufacturers with precise ballistic coefficients. Select your manufacturer, caliber, and bullet to automatically populate weight and BC values."
    },
    {
      title: "Field Data Shooting Points",
      content: "Track your real-world shooting data by adding field observations. This helps validate your calculator results with actual shooting conditions and performance."
    },
    {
      title: "Environmental Conditions",
      content: "Enter current shooting conditions: elevation, temperature, barometric pressure, humidity, and wind speed. These significantly affect bullet trajectory."
    },
    {
      title: "Calculate and Compare",
      content: "Generate ballistic data and compare it with your field observations to verify accuracy and make adjustments for your specific rifle and conditions."
    }
  ];

  // Bullet database — sourced from BC CALC BULLET BC'S.xlsx (client-supplied audit).
  // G1/G7 BCs are stored per-bullet; G7 values follow the legacy bccalculator.com
  // convention (G1 × 0.512 for non-Hornady/Speer entries, real published values otherwise).
  // Discontinued bullets (red-flagged in the source spreadsheet) have been removed.
  const bulletDatabase = {
    'Hornady': {
      '.172': [
        { name: '20gr V-MAX', weight: 20, bcG1: 0.185, bcG7: 0.09472 }
      ],
      '.204': [
        { name: '24gr NTX', weight: 24, bcG1: 0.17, bcG7: 0.08704 },
        { name: '32gr V-MAX', weight: 32, bcG1: 0.21, bcG7: 0.10752 },
        { name: '40gr V-MAX', weight: 40, bcG1: 0.275, bcG7: 0.1408 }
      ],
      '.224': [
        { name: '35gr V-MAX', weight: 35, bcG1: 0.109, bcG7: 0.055808 },
        { name: '35gr NTX', weight: 35, bcG1: 0.177, bcG7: 0.090624 },
        { name: '40gr V-MAX', weight: 40, bcG1: 0.2, bcG7: 0.1024 },
        { name: '50gr V-MAX', weight: 50, bcG1: 0.242, bcG7: 0.123904 },
        { name: '53gr V-MAX', weight: 53, bcG1: 0.29, bcG7: 0.14848 },
        { name: '55gr V-MAX', weight: 55, bcG1: 0.255, bcG7: 0.13056 },
        { name: '55gr FMJ-BT', weight: 55, bcG1: 0.243, bcG7: 0.124416 },
        { name: '60gr V-MAX', weight: 60, bcG1: 0.265, bcG7: 0.13568 },
        { name: '68gr BTHP', weight: 68, bcG1: 0.355, bcG7: 0.18176 },
        { name: '73gr ELD-M', weight: 73, bcG1: 0.398, bcG7: 0.2 },
        { name: '75gr ELD-M', weight: 75, bcG1: 0.467, bcG7: 0.235 },
        { name: '76gr A-MAX', weight: 76, bcG1: 0.413, bcG7: 0.208 },
        { name: '80gr ELD-M', weight: 80, bcG1: 0.485, bcG7: 0.244 },
        { name: '88gr ELD-M', weight: 88, bcG1: 0.545, bcG7: 0.274 }
      ],
      '.243': [
        { name: '65gr V-MAX', weight: 65, bcG1: 0.28, bcG7: 0.14336 },
        { name: '75gr V-MAX', weight: 75, bcG1: 0.33, bcG7: 0.16896 },
        { name: '87gr V-MAX', weight: 87, bcG1: 0.4, bcG7: 0.2048 },
        { name: '90gr ELD-X', weight: 90, bcG1: 0.409, bcG7: 0.206 },
        { name: '95gr SST', weight: 95, bcG1: 0.355, bcG7: 0.18176 },
        { name: '100gr BTSP InterLock', weight: 100, bcG1: 0.405, bcG7: 0.20736 },
        { name: '103gr ELD-X', weight: 103, bcG1: 0.512, bcG7: 0.258 },
        { name: '105gr A-MAX', weight: 105, bcG1: 0.536, bcG7: 0.274432 },
        { name: '108gr ELD-M', weight: 108, bcG1: 0.536, bcG7: 0.27 },
        { name: '110gr A-TIP Match', weight: 110, bcG1: 0.604, bcG7: 0.304 }
      ],
      '.257': [
        { name: '75gr V-MAX', weight: 75, bcG1: 0.29, bcG7: 0.14848 },
        { name: '110gr ELD-X', weight: 110, bcG1: 0.465, bcG7: 0.234 },
        { name: '117gr SST', weight: 117, bcG1: 0.39, bcG7: 0.19968 },
        { name: '128gr ELD-X', weight: 128, bcG1: 0.633, bcG7: 0.319 },
        { name: '134gr ELD-M', weight: 134, bcG1: 0.645, bcG7: 0.325 },
        { name: '138gr A-TIP Match', weight: 138, bcG1: 0.695, bcG7: 0.35 }
      ],
      '.264': [
        { name: '95gr V-MAX', weight: 95, bcG1: 0.365, bcG7: 0.18688 },
        { name: '120gr ELD-M', weight: 120, bcG1: 0.486, bcG7: 0.245 },
        { name: '123gr ELD-M', weight: 123, bcG1: 0.506, bcG7: 0.255 },
        { name: '129gr SST', weight: 129, bcG1: 0.485, bcG7: 0.24832 },
        { name: '130gr ELD-M', weight: 130, bcG1: 0.554, bcG7: 0.279 },
        { name: '135gr A-TIP Match', weight: 135, bcG1: 0.637, bcG7: 0.321 },
        { name: '140gr A-MAX', weight: 140, bcG1: 0.608, bcG7: 0.311296 },
        { name: '140gr SST', weight: 140, bcG1: 0.52, bcG7: 0.26624 },
        { name: '140gr ELD-M', weight: 140, bcG1: 0.646, bcG7: 0.326 },
        { name: '143gr ELD-X', weight: 143, bcG1: 0.625, bcG7: 0.315 },
        { name: '147gr ELD-M', weight: 147, bcG1: 0.697, bcG7: 0.351 },
        { name: '153gr A-TIP Match', weight: 153, bcG1: 0.704, bcG7: 0.355 }
      ],
      '.277': [
        { name: '110gr V-MAX', weight: 110, bcG1: 0.37, bcG7: 0.18944 },
        { name: '130gr SST', weight: 130, bcG1: 0.46, bcG7: 0.23552 },
        { name: '140gr SST', weight: 140, bcG1: 0.495, bcG7: 0.25344 },
        { name: '145gr ELD-X', weight: 145, bcG1: 0.536, bcG7: 0.27 },
        { name: '150gr SST', weight: 150, bcG1: 0.525, bcG7: 0.2688 }
      ],
      '.284': [
        { name: '139gr SST', weight: 139, bcG1: 0.486, bcG7: 0.248832 },
        { name: '150gr ELD-X', weight: 150, bcG1: 0.574, bcG7: 0.289 },
        { name: '154gr SST', weight: 154, bcG1: 0.525, bcG7: 0.2688 },
        { name: '162gr SST', weight: 162, bcG1: 0.55, bcG7: 0.2816 },
        { name: '162gr ELD-X', weight: 162, bcG1: 0.63, bcG7: 0.315 },
        { name: '175gr ELD-X', weight: 175, bcG1: 0.589, bcG7: 0.347 },
        { name: '180gr ELD-M', weight: 180, bcG1: 0.796, bcG7: 0.401 },
        { name: '190gr A-TIP Match', weight: 190, bcG1: 0.838, bcG7: 0.422 }
      ],
      '.308': [
        { name: '110gr V-MAX', weight: 110, bcG1: 0.29, bcG7: 0.14848 },
        { name: '125gr SST', weight: 125, bcG1: 0.305, bcG7: 0.15616 },
        { name: '150gr SST', weight: 150, bcG1: 0.415, bcG7: 0.21248 },
        { name: '155gr ELD-M', weight: 155, bcG1: 0.461, bcG7: 0.232 },
        { name: '165gr SST', weight: 165, bcG1: 0.447, bcG7: 0.228864 },
        { name: '168gr ELD-M', weight: 168, bcG1: 0.523, bcG7: 0.263 },
        { name: '168gr A-MAX', weight: 168, bcG1: 0.44, bcG7: 0.22528 },
        { name: '178gr ELD-X', weight: 178, bcG1: 0.552, bcG7: 0.278 },
        { name: '178gr ELD-M', weight: 178, bcG1: 0.547, bcG7: 0.275 },
        { name: '178gr A-MAX', weight: 178, bcG1: 0.485, bcG7: 0.24832 },
        { name: '180gr SST', weight: 180, bcG1: 0.48, bcG7: 0.24576 },
        { name: '200gr ELD-X', weight: 200, bcG1: 0.597, bcG7: 0.301 },
        { name: '208gr ELD-M', weight: 208, bcG1: 0.69, bcG7: 0.348 },
        { name: '212gr ELD-X', weight: 212, bcG1: 0.663, bcG7: 0.334 },
        { name: '220gr ELD-X', weight: 220, bcG1: 0.654, bcG7: 0.325 },
        { name: '225gr ELD-M', weight: 225, bcG1: 0.777, bcG7: 0.391 },
        { name: '230gr A-TIP Match', weight: 230, bcG1: 0.823, bcG7: 0.414 },
        { name: '250gr A-TIP Match', weight: 250, bcG1: 0.878, bcG7: 0.442 }
      ],
      '.323': [
        { name: '150gr InterLock', weight: 150, bcG1: 0.29, bcG7: 0.14848 },
        { name: '170gr InterLock', weight: 170, bcG1: 0.217, bcG7: 0.111104 },
        { name: '170gr SST', weight: 170, bcG1: 0.445, bcG7: 0.22784 },
        { name: '195gr InterLock', weight: 195, bcG1: 0.41, bcG7: 0.20992 }
      ],
      '.338': [
        { name: '200gr SST', weight: 200, bcG1: 0.455, bcG7: 0.23296 },
        { name: '225gr SST', weight: 225, bcG1: 0.515, bcG7: 0.26368 },
        { name: '225gr InterLock', weight: 225, bcG1: 0.397, bcG7: 0.203264 },
        { name: '230gr ELD-X', weight: 230, bcG1: 0.616, bcG7: 0.31 },
        { name: '270gr ELD-X', weight: 270, bcG1: 0.757, bcG7: 0.381 },
        { name: '285gr ELD-M', weight: 285, bcG1: 0.829, bcG7: 0.417 },
        { name: '300gr A-TIP Match', weight: 300, bcG1: 0.863, bcG7: 0.435 }
      ],
      '.375': [
        { name: '250gr CX', weight: 250, bcG1: 0.403, bcG7: 0.206336 },
        { name: '300gr DGS', weight: 300, bcG1: 0.275, bcG7: 0.1408 },
        { name: '390gr A-TIP Match', weight: 390, bcG1: 0.987, bcG7: 0.497 }
      ],
      '.416': [
        { name: '400gr DGS', weight: 400, bcG1: 0.319, bcG7: 0.163328 },
        { name: '400gr DGX Bonded', weight: 400, bcG1: 0.319, bcG7: 0.163328 },
        { name: '500gr A-TIP Match', weight: 500, bcG1: 0.976, bcG7: 0.492 }
      ],
      '.458': [
        { name: '480gr DGS', weight: 480, bcG1: 0.285, bcG7: 0.14592 },
        { name: '480gr DGX Bonded', weight: 480, bcG1: 0.285, bcG7: 0.14592 },
        { name: '500gr DGS', weight: 500, bcG1: 0.295, bcG7: 0.15104 },
        { name: '500gr DGX Bonded', weight: 500, bcG1: 0.295, bcG7: 0.15104 }
      ]
    },
    'Sierra': {
      '.204': [
        { name: '32gr BlitzKing', weight: 32, bcG1: 0.221, bcG7: 0.113152 },
        { name: '39gr BlitzKing', weight: 39, bcG1: 0.287, bcG7: 0.146944 }
      ],
      '.223': [
        { name: '40gr Varminter Hornet', weight: 40, bcG1: 0.117, bcG7: 0.059904 },
        { name: '45gr Varminter Hornet', weight: 45, bcG1: 0.132, bcG7: 0.067584 }
      ],
      '.224': [
        { name: '40gr BlitzKing', weight: 40, bcG1: 0.221, bcG7: 0.113152 },
        { name: '40gr Varminter HP', weight: 40, bcG1: 0.199, bcG7: 0.101888 },
        { name: '40gr Varminter Hornet', weight: 40, bcG1: 0.118, bcG7: 0.060416 },
        { name: '45gr Varminter Hornet', weight: 45, bcG1: 0.146, bcG7: 0.074752 },
        { name: '45gr Varminter SPT', weight: 45, bcG1: 0.192, bcG7: 0.098304 },
        { name: '50gr BlitzKing', weight: 50, bcG1: 0.271, bcG7: 0.138752 },
        { name: '50gr Varminter', weight: 50, bcG1: 0.259, bcG7: 0.132608 },
        { name: '52gr MatchKing HPBT', weight: 52, bcG1: 0.229, bcG7: 0.117248 },
        { name: '53gr MatchKing HPBT', weight: 53, bcG1: 0.224, bcG7: 0.114688 },
        { name: '55gr BlitzKing', weight: 55, bcG1: 0.271, bcG7: 0.138752 },
        { name: '55gr GameKing FMJBT', weight: 55, bcG1: 0.25, bcG7: 0.128 },
        { name: '55gr GameKing HPBT', weight: 55, bcG1: 0.271, bcG7: 0.138752 },
        { name: '55gr GameKing SBT', weight: 55, bcG1: 0.25, bcG7: 0.128 },
        { name: '55gr Varminter SPT', weight: 55, bcG1: 0.255, bcG7: 0.13056 },
        { name: '60gr Varminter HP', weight: 60, bcG1: 0.246, bcG7: 0.125952 },
        { name: '63gr Varminter SMP', weight: 63, bcG1: 0.303, bcG7: 0.155136 },
        { name: '65gr GameKing SBT', weight: 65, bcG1: 0.303, bcG7: 0.155136 },
        { name: '69gr MatchKing HPBT', weight: 69, bcG1: 0.301, bcG7: 0.154112 },
        { name: '77gr MatchKing HPBT', weight: 77, bcG1: 0.362, bcG7: 0.185344 },
        { name: '80gr MatchKing HPBT', weight: 80, bcG1: 0.4, bcG7: 0.2048 },
        { name: '90gr MatchKing HPBT', weight: 90, bcG1: 0.551, bcG7: 0.282112 }
      ],
      '.243': [
        { name: '55gr BlitzKing', weight: 55, bcG1: 0.271, bcG7: 0.138752 },
        { name: '60gr Varminter HP', weight: 60, bcG1: 0.246, bcG7: 0.125952 },
        { name: '70gr BlitzKing', weight: 70, bcG1: 0.299, bcG7: 0.153088 },
        { name: '70gr MatchKing', weight: 70, bcG1: 0.301, bcG7: 0.154112 },
        { name: '75gr Varminter HP', weight: 75, bcG1: 0.33, bcG7: 0.16896 },
        { name: '85gr Varminter SPT', weight: 85, bcG1: 0.377, bcG7: 0.193024 },
        { name: '90gr MatchKing HPBT', weight: 90, bcG1: 0.551, bcG7: 0.282112 },
        { name: '100gr GameKing SBT', weight: 100, bcG1: 0.43, bcG7: 0.22016 },
        { name: '100gr Pro-Hunter SPT', weight: 100, bcG1: 0.356, bcG7: 0.182272 },
        { name: '107gr MatchKing HPBT', weight: 107, bcG1: 0.527, bcG7: 0.269824 }
      ],
      '.257': [
        { name: '75gr BlitzKing', weight: 75, bcG1: 0.36, bcG7: 0.18432 },
        { name: '87gr Varminter SPT', weight: 87, bcG1: 0.376, bcG7: 0.192512 },
        { name: '90gr BlitzKing', weight: 90, bcG1: 0.504, bcG7: 0.258048 },
        { name: '90gr GameKing HPBT', weight: 90, bcG1: 0.365, bcG7: 0.18688 },
        { name: '100gr GameKing SBT', weight: 100, bcG1: 0.43, bcG7: 0.22016 },
        { name: '100gr MatchKing', weight: 100, bcG1: 0.405, bcG7: 0.20736 },
        { name: '117gr GameKing SBT', weight: 117, bcG1: 0.41, bcG7: 0.20992 }
      ],
      '.264': [
        { name: '100gr BlitzKing', weight: 100, bcG1: 0.429, bcG7: 0.219648 },
        { name: '100gr MatchKing HP', weight: 100, bcG1: 0.361, bcG7: 0.184832 },
        { name: '107gr MatchKing HPBT', weight: 107, bcG1: 0.527, bcG7: 0.269824 },
        { name: '120gr GameKing HPBT', weight: 120, bcG1: 0.458, bcG7: 0.234496 },
        { name: '120gr Pro-Hunter SPT', weight: 120, bcG1: 0.356, bcG7: 0.182272 },
        { name: '130gr GameKing SBT', weight: 130, bcG1: 0.46, bcG7: 0.23552 },
        { name: '140gr GameKing SBT', weight: 140, bcG1: 0.495, bcG7: 0.25344 },
        { name: '140gr MatchKing', weight: 140, bcG1: 0.52, bcG7: 0.26624 },
        { name: '142gr MatchKing HPBT', weight: 142, bcG1: 0.58, bcG7: 0.29696 },
        { name: '155gr MatchKing', weight: 155, bcG1: 0.49, bcG7: 0.25088 }
      ],
      '.277': [
        { name: '110gr GameKing HPBT', weight: 110, bcG1: 0.31, bcG7: 0.15872 },
        { name: '115gr MatchKing HPBT', weight: 115, bcG1: 0.466, bcG7: 0.238592 },
        { name: '130gr GameKing SBT', weight: 130, bcG1: 0.435, bcG7: 0.22272 },
        { name: '130gr Pro-Hunter SPT', weight: 130, bcG1: 0.336, bcG7: 0.172032 },
        { name: '135gr MatchKing', weight: 135, bcG1: 0.475, bcG7: 0.2432 },
        { name: '140gr GameKing HPBT', weight: 140, bcG1: 0.416, bcG7: 0.212992 },
        { name: '140gr GameKing SBT', weight: 140, bcG1: 0.495, bcG7: 0.25344 },
        { name: '150gr GameKing SBT', weight: 150, bcG1: 0.52, bcG7: 0.26624 }
      ],
      '.284': [
        { name: '100gr Varminter HP', weight: 100, bcG1: 0.209, bcG7: 0.107008 },
        { name: '120gr BlitzKing', weight: 120, bcG1: 0.417, bcG7: 0.213504 },
        { name: '120gr Pro-Hunter SPT', weight: 120, bcG1: 0.356, bcG7: 0.182272 },
        { name: '130gr MatchKing', weight: 130, bcG1: 0.485, bcG7: 0.24832 },
        { name: '140gr GameKing SBT', weight: 140, bcG1: 0.495, bcG7: 0.25344 },
        { name: '140gr Pro-Hunter SPT', weight: 140, bcG1: 0.387, bcG7: 0.198144 },
        { name: '150gr GameKing SBT', weight: 150, bcG1: 0.519, bcG7: 0.265728 },
        { name: '150gr MatchKing', weight: 150, bcG1: 0.48, bcG7: 0.24576 },
        { name: '160gr GameKing HPBT', weight: 160, bcG1: 0.394, bcG7: 0.201728 },
        { name: '160gr GameKing SBT', weight: 160, bcG1: 0.472, bcG7: 0.241664 },
        { name: '168gr GameKing', weight: 168, bcG1: 0.462, bcG7: 0.236544 },
        { name: '175gr GameKing SBT', weight: 175, bcG1: 0.519, bcG7: 0.265728 },
        { name: '175gr MatchKing HPBT', weight: 175, bcG1: 0.496, bcG7: 0.253952 }
      ],
      '.308': [
        { name: '110gr Varminter HP', weight: 110, bcG1: 0.174, bcG7: 0.089088 },
        { name: '(.30-.30) 125gr Pro-Hunter HP/FN', weight: 125, bcG1: 0.186, bcG7: 0.095232 },
        { name: '125gr GameKing SBT', weight: 125, bcG1: 0.366, bcG7: 0.187392 },
        { name: '125gr Pro-Hunter SPT', weight: 125, bcG1: 0.267, bcG7: 0.136704 },
        { name: '(.30-.30) 150gr Pro-Hunter FN', weight: 150, bcG1: 0.193, bcG7: 0.098816 },
        { name: '150gr GameKing SBT', weight: 150, bcG1: 0.435, bcG7: 0.22272 },
        { name: '150gr MatchKing', weight: 150, bcG1: 0.48, bcG7: 0.24576 },
        { name: '150gr Pro-Hunter SPT', weight: 150, bcG1: 0.314, bcG7: 0.160768 },
        { name: '155gr MatchKing HPBT', weight: 155, bcG1: 0.443, bcG7: 0.226816 },
        { name: '165gr GameKing HPBT', weight: 165, bcG1: 0.404, bcG7: 0.206848 },
        { name: '165gr GameKing SBT', weight: 165, bcG1: 0.447, bcG7: 0.228864 },
        { name: '168gr MatchKing HPBT', weight: 168, bcG1: 0.462, bcG7: 0.236544 },
        { name: '(.30-.30) 170gr MatchKing FN Palma', weight: 170, bcG1: 0.254, bcG7: 0.130048 },
        { name: '175gr MatchKing HPBT', weight: 175, bcG1: 0.496, bcG7: 0.253952 },
        { name: '180gr GameKing SBT', weight: 180, bcG1: 0.507, bcG7: 0.259584 },
        { name: '180gr MatchKing', weight: 180, bcG1: 0.465, bcG7: 0.23808 },
        { name: '190gr MatchKing HPBT', weight: 190, bcG1: 0.52, bcG7: 0.26624 },
        { name: '200gr GameKing SBT', weight: 200, bcG1: 0.565, bcG7: 0.28928 },
        { name: '200gr MatchKing HPBT', weight: 200, bcG1: 0.552, bcG7: 0.282624 },
        { name: '220gr MatchKing HPBT', weight: 220, bcG1: 0.608, bcG7: 0.311296 },
        { name: '240gr MatchKing HPBT', weight: 240, bcG1: 0.69, bcG7: 0.35328 }
      ],
      '.323': [
        { name: '200gr MatchKing', weight: 200, bcG1: 0.552, bcG7: 0.282624 },
        { name: '220gr GameKing SBT', weight: 220, bcG1: 0.629, bcG7: 0.322048 }
      ],
      '.338': [
        { name: '200gr GameKing SBT', weight: 200, bcG1: 0.565, bcG7: 0.28928 },
        { name: '215gr MatchKing HPBT', weight: 215, bcG1: 0.68, bcG7: 0.34816 },
        { name: '225gr GameKing SBT', weight: 225, bcG1: 0.588, bcG7: 0.301056 },
        { name: '250gr GameKing SBT', weight: 250, bcG1: 0.67, bcG7: 0.34304 },
        { name: '250gr MatchKing HPBT', weight: 250, bcG1: 0.69, bcG7: 0.35328 },
        { name: '300gr MatchKing HPBT', weight: 300, bcG1: 0.73, bcG7: 0.37376 }
      ],
      '.358': [
        { name: '225gr GameKing SBT', weight: 225, bcG1: 0.588, bcG7: 0.301056 }
      ],
      '.375': [
        { name: '200gr Pro-Hunter FN', weight: 200, bcG1: 0.246, bcG7: 0.125952 },
        { name: '250gr GameKing SBT', weight: 250, bcG1: 0.67, bcG7: 0.34304 },
        { name: '300gr GameKing SBT', weight: 300, bcG1: 0.73, bcG7: 0.37376 }
      ],
      '.458': [
        { name: '(45-70) 300gr Pro-Hunter HP/FN', weight: 300, bcG1: 0.248, bcG7: 0.126976 }
      ]
    },
    'Berger': {
      '.204': [
        { name: '35gr Varmint', weight: 35, bcG1: 0.179, bcG7: 0.091648 }
      ],
      '.224': [
        { name: '52gr FB Target', weight: 52, bcG1: 0.235, bcG7: 0.12032 },
        { name: '55gr FB Varmint', weight: 55, bcG1: 0.209, bcG7: 0.107008 },
        { name: '60gr Varmint', weight: 60, bcG1: 0.271, bcG7: 0.138752 },
        { name: '70gr VLD Target', weight: 70, bcG1: 0.374, bcG7: 0.191488 },
        { name: '73gr BT Target', weight: 73, bcG1: 0.348, bcG7: 0.178176 },
        { name: '77gr OTM Tactical', weight: 77, bcG1: 0.374, bcG7: 0.191488 },
        { name: '80gr VLD Target', weight: 80, bcG1: 0.455, bcG7: 0.23296 },
        { name: '90gr VLD Target', weight: 90, bcG1: 0.534, bcG7: 0.273408 }
      ],
      '.243': [
        { name: '65gr BT', weight: 65, bcG1: 0.299, bcG7: 0.153088 },
        { name: '80gr Varmint', weight: 80, bcG1: 0.36, bcG7: 0.18432 },
        { name: '87gr VLD Target', weight: 87, bcG1: 0.47, bcG7: 0.24064 },
        { name: '90gr BT', weight: 90, bcG1: 0.39, bcG7: 0.19968 },
        { name: '95gr VLD Hunting', weight: 95, bcG1: 0.486, bcG7: 0.248832 },
        { name: '105gr VLD Hunting', weight: 105, bcG1: 0.536, bcG7: 0.274432 },
        { name: '105gr VLD Target', weight: 105, bcG1: 0.536, bcG7: 0.274432 },
        { name: '108gr BT Target', weight: 108, bcG1: 0.522, bcG7: 0.267264 },
        { name: '115gr VLD Target', weight: 115, bcG1: 0.563, bcG7: 0.288256 }
      ],
      '.257': [
        { name: '87gr VLD Target', weight: 87, bcG1: 0.47, bcG7: 0.24064 },
        { name: '115gr VLD Target', weight: 115, bcG1: 0.563, bcG7: 0.288256 }
      ],
      '.264': [
        { name: '130gr VLD Hunting', weight: 130, bcG1: 0.562, bcG7: 0.287744 },
        { name: '140gr VLD Target', weight: 140, bcG1: 0.6, bcG7: 0.3072 },
        { name: '140gr VLD Hunting', weight: 140, bcG1: 0.6, bcG7: 0.3072 },
        { name: '156gr VLD Target', weight: 156, bcG1: 0.679, bcG7: 0.347648 }
      ],
      '.277': [
        { name: '130gr Classic Hunter', weight: 130, bcG1: 0.49, bcG7: 0.25088 },
        { name: '130gr VLD Hunting', weight: 130, bcG1: 0.462, bcG7: 0.236544 },
        { name: '140gr VLD Hunting', weight: 140, bcG1: 0.504, bcG7: 0.258048 },
        { name: '150gr VLD Hunting', weight: 150, bcG1: 0.505, bcG7: 0.25856 },
        { name: '180gr VLD Hunting', weight: 180, bcG1: 0.655, bcG7: 0.33536 }
      ],
      '.284': [
        { name: '140gr VLD Hunting', weight: 140, bcG1: 0.6, bcG7: 0.3072 },
        { name: '168gr VLD Hunting', weight: 168, bcG1: 0.5, bcG7: 0.256 },
        { name: '175gr Elite Hunter', weight: 175, bcG1: 0.61, bcG7: 0.31232 },
        { name: '180gr VLD Hunting', weight: 180, bcG1: 0.655, bcG7: 0.33536 },
        { name: '180gr VLD Target', weight: 180, bcG1: 0.655, bcG7: 0.33536 }
      ],
      '.308': [
        { name: '150gr Target', weight: 150, bcG1: 0.464, bcG7: 0.237568 },
        { name: '155gr VLD Hunting', weight: 155, bcG1: 0.464, bcG7: 0.237568 },
        { name: '155gr VLD Target', weight: 155, bcG1: 0.464, bcG7: 0.237568 },
        { name: '168gr VLD Hunting', weight: 168, bcG1: 0.498, bcG7: 0.254976 },
        { name: '168gr VLD Target', weight: 168, bcG1: 0.498, bcG7: 0.254976 },
        { name: '168gr Hybrid Target', weight: 168, bcG1: 0.489, bcG7: 0.250368 },
        { name: '175gr VLD Hunting', weight: 175, bcG1: 0.518, bcG7: 0.265216 },
        { name: '175gr OTM Tactical', weight: 175, bcG1: 0.505, bcG7: 0.25856 },
        { name: '185gr VLD Hunting', weight: 185, bcG1: 0.558, bcG7: 0.285696 },
        { name: '185gr Hybrid Target', weight: 185, bcG1: 0.558, bcG7: 0.285696 },
        { name: '185gr Juggernaut Target', weight: 185, bcG1: 0.56, bcG7: 0.28672 },
        { name: '190gr VLD Hunting', weight: 190, bcG1: 0.52, bcG7: 0.26624 },
        { name: '190gr VLD Target', weight: 190, bcG1: 0.52, bcG7: 0.26624 },
        { name: '195gr EOL Elite Hunter', weight: 195, bcG1: 0.755, bcG7: 0.38656 },
        { name: '200gr Hybrid Target', weight: 200, bcG1: 0.565, bcG7: 0.28928 },
        { name: '208gr ELR Target', weight: 208, bcG1: 0.64, bcG7: 0.32768 },
        { name: '210gr VLD Hunting', weight: 210, bcG1: 0.6, bcG7: 0.3072 },
        { name: '210gr VLD Target', weight: 210, bcG1: 0.6, bcG7: 0.3072 },
        { name: '215gr Hybrid Target', weight: 215, bcG1: 0.61, bcG7: 0.31232 },
        { name: '230gr Hybrid Target', weight: 230, bcG1: 0.68, bcG7: 0.34816 }
      ],
      '.338': [
        { name: '250gr Elite Hunter', weight: 250, bcG1: 0.685, bcG7: 0.35072 },
        { name: '300gr Elite Hunter', weight: 300, bcG1: 0.814, bcG7: 0.416768 },
        { name: '300gr OTM Tactical', weight: 300, bcG1: 0.71, bcG7: 0.36352 }
      ]
    },
    'Nosler': {
      '.204': [
        { name: '40gr Ballistic Tip', weight: 40, bc: 0.239 }
      ],
      '.224': [
        { name: '40gr Ballistic Tip', weight: 40, bc: 0.221 },
        { name: '50gr Ballistic Tip', weight: 50, bc: 0.238 },
        { name: '55gr Ballistic Tip', weight: 55, bc: 0.267 },
        { name: '60gr Partition', weight: 60, bc: 0.265 },
        { name: '64gr Bonded Performance', weight: 64, bc: 0.327 },
        { name: '69gr Custom Competition', weight: 69, bc: 0.338 },
        { name: '77gr Custom Competition', weight: 77, bc: 0.362 },
        { name: '80gr Custom Competition', weight: 80, bc: 0.415 }
      ],
      '.243': [
        { name: '45gr Ballistic SilverTip', weight: 45, bc: 0.184 },
        { name: '45gr Ballistic Tip', weight: 45, bc: 0.184 },
        { name: '70gr Ballistic Tip', weight: 70, bc: 0.310 },
        { name: '80gr Ballistic Tip', weight: 80, bc: 0.329 },
        { name: '90gr E-Tip', weight: 90, bc: 0.329 },
        { name: '95gr Ballistic Tip', weight: 95, bc: 0.379 },
        { name: '100gr Partition', weight: 100, bc: 0.357 },
        { name: '105gr Custom Competition', weight: 105, bc: 0.527 }
      ],
      '.257': [
        { name: '85gr Ballistic SilverTip', weight: 85, bc: 0.329 },
        { name: '85gr Ballistic Tip', weight: 85, bc: 0.329 },
        { name: '100gr Ballistic Tip', weight: 100, bc: 0.393 },
        { name: '100gr Partition', weight: 100, bc: 0.377 },
        { name: '100gr AccuBond', weight: 100, bc: 0.420 },
        { name: '110gr AccuBond', weight: 110, bc: 0.418 },
        { name: '115gr Ballistic Tip', weight: 115, bc: 0.453 },
        { name: '120gr Partition', weight: 120, bc: 0.458 }
      ],
      '.264': [
        { name: '110gr Ballistic Tip', weight: 110, bc: 0.370 },
        { name: '110gr Partition', weight: 110, bc: 0.380 },
        { name: '120gr Ballistic Tip', weight: 120, bc: 0.458 },
        { name: '125gr Partition', weight: 125, bc: 0.449 },
        { name: '129gr AccuBond LR', weight: 129, bc: 0.530 },
        { name: '130gr AccuBond', weight: 130, bc: 0.488 },
        { name: '140gr AccuBond', weight: 140, bc: 0.509 },
        { name: '140gr Partition', weight: 140, bc: 0.490 },
        { name: '142gr AccuBond LR', weight: 142, bc: 0.719 }
      ],
      '.277': [
        { name: '130gr Ballistic SilverTip', weight: 130, bc: 0.436 },
        { name: '130gr Ballistic Tip', weight: 130, bc: 0.433 },
        { name: '130gr Partition', weight: 130, bc: 0.416 },
        { name: '130gr AccuBond', weight: 130, bc: 0.488 },
        { name: '140gr Ballistic Tip', weight: 140, bc: 0.456 },
        { name: '140gr AccuBond', weight: 140, bc: 0.509 },
        { name: '150gr Ballistic Tip', weight: 150, bc: 0.496 },
        { name: '150gr Partition', weight: 150, bc: 0.465 },
        { name: '150gr AccuBond', weight: 150, bc: 0.531 }
      ],
      '.284': [
        { name: '140gr Ballistic Tip', weight: 140, bc: 0.485 },
        { name: '140gr Partition', weight: 140, bc: 0.434 },
        { name: '140gr AccuBond', weight: 140, bc: 0.509 },
        { name: '150gr Ballistic Tip', weight: 150, bc: 0.493 },
        { name: '150gr Partition', weight: 150, bc: 0.465 },
        { name: '150gr AccuBond LR', weight: 150, bc: 0.611 },
        { name: '160gr AccuBond', weight: 160, bc: 0.531 },
        { name: '168gr AccuBond LR', weight: 168, bc: 0.648 }
      ],
      '.308': [
        { name: '125gr Ballistic Tip', weight: 125, bc: 0.366 },
        { name: '150gr Ballistic Tip', weight: 150, bc: 0.435 },
        { name: '150gr Partition', weight: 150, bc: 0.387 },
        { name: '150gr AccuBond LR', weight: 150, bc: 0.591 },
        { name: '165gr Ballistic Tip', weight: 165, bc: 0.475 },
        { name: '165gr Partition', weight: 165, bc: 0.410 },
        { name: '165gr AccuBond', weight: 165, bc: 0.475 },
        { name: '168gr Custom Competition', weight: 168, bc: 0.462 },
        { name: '180gr Ballistic Tip', weight: 180, bc: 0.507 },
        { name: '180gr Partition', weight: 180, bc: 0.474 },
        { name: '180gr AccuBond', weight: 180, bc: 0.507 },
        { name: '190gr AccuBond LR', weight: 190, bc: 0.640 },
        { name: '200gr Partition', weight: 200, bc: 0.481 },
        { name: '210gr AccuBond LR', weight: 210, bc: 0.730 },
        { name: '220gr Partition', weight: 220, bc: 0.351 }
      ],
      '.323': [
        { name: '200gr AccuBond', weight: 200, bc: 0.546 }
      ],
      '.338': [
        { name: '180gr AccuBond', weight: 180, bc: 0.372 },
        { name: '200gr AccuBond', weight: 200, bc: 0.414 },
        { name: '225gr Partition', weight: 225, bc: 0.454 },
        { name: '225gr AccuBond', weight: 225, bc: 0.548 },
        { name: '250gr AccuBond', weight: 250, bc: 0.575 },
        { name: '300gr AccuBond', weight: 300, bc: 0.720 }
      ],
      '.375': [
        { name: '260gr Ballistic Tip', weight: 260, bc: 0.473 },
        { name: '260gr Partition', weight: 260, bc: 0.314 },
        { name: '260gr AccuBond', weight: 260, bc: 0.473 },
        { name: '300gr Partition', weight: 300, bc: 0.398 }
      ]
    },
    'Barnes': {
      '.224': [
        { name: '40gr Varmint-A-Tor', weight: 40, bcG1: 0.129, bcG7: 0.066048 },
        { name: '50gr Varmint Grenade', weight: 50, bcG1: 0.191, bcG7: 0.097792 },
        { name: '53gr TSX', weight: 53, bcG1: 0.205, bcG7: 0.10496 },
        { name: '55gr TSX', weight: 55, bcG1: 0.237, bcG7: 0.121344 },
        { name: '62gr TSX', weight: 62, bcG1: 0.294, bcG7: 0.150528 },
        { name: '70gr TSX', weight: 70, bcG1: 0.323, bcG7: 0.165376 }
      ],
      '.243': [
        { name: '58gr Varmint-A-Tor', weight: 58, bcG1: 0.138, bcG7: 0.070656 },
        { name: '72gr Varmint-A-Tor', weight: 72, bcG1: 0.21, bcG7: 0.10752 },
        { name: '80gr TTSX', weight: 80, bcG1: 0.33, bcG7: 0.16896 },
        { name: '85gr TSX', weight: 85, bcG1: 0.33, bcG7: 0.16896 },
        { name: '95gr LRX', weight: 95, bcG1: 0.383, bcG7: 0.196096 }
      ],
      '.257': [
        { name: '100gr TTSX', weight: 100, bcG1: 0.369, bcG7: 0.188928 },
        { name: '115gr TSX', weight: 115, bcG1: 0.389, bcG7: 0.199168 }
      ],
      '.264': [
        { name: '120gr TTSX', weight: 120, bcG1: 0.412, bcG7: 0.210944 },
        { name: '127gr LRX', weight: 127, bcG1: 0.468, bcG7: 0.239616 },
        { name: '140gr TSX', weight: 140, bcG1: 0.404, bcG7: 0.206848 }
      ],
      '.277': [
        { name: '110gr TSX', weight: 110, bcG1: 0.331, bcG7: 0.169472 },
        { name: '129gr LRX', weight: 129, bcG1: 0.443, bcG7: 0.226816 },
        { name: '130gr TTSX', weight: 130, bcG1: 0.35, bcG7: 0.1792 },
        { name: '139gr LRX', weight: 139, bcG1: 0.486, bcG7: 0.248832 },
        { name: '150gr TSX', weight: 150, bcG1: 0.442, bcG7: 0.226304 }
      ],
      '.284': [
        { name: '120gr TSX', weight: 120, bcG1: 0.345, bcG7: 0.17664 },
        { name: '140gr TSX', weight: 140, bcG1: 0.404, bcG7: 0.206848 },
        { name: '150gr TTSX', weight: 150, bcG1: 0.45, bcG7: 0.2304 },
        { name: '160gr TSX', weight: 160, bcG1: 0.45, bcG7: 0.2304 },
        { name: '168gr LRX', weight: 168, bcG1: 0.55, bcG7: 0.2816 }
      ],
      '.308': [
        { name: '130gr TSX', weight: 130, bcG1: 0.35, bcG7: 0.1792 },
        { name: '150gr TTSX', weight: 150, bcG1: 0.442, bcG7: 0.226304 },
        { name: '165gr TTSX', weight: 165, bcG1: 0.447, bcG7: 0.228864 },
        { name: '168gr TSX', weight: 168, bcG1: 0.404, bcG7: 0.206848 },
        { name: '175gr LRX', weight: 175, bcG1: 0.508, bcG7: 0.260096 },
        { name: '180gr TSX', weight: 180, bcG1: 0.484, bcG7: 0.247808 },
        { name: '190gr LRX', weight: 190, bcG1: 0.54, bcG7: 0.27648 },
        { name: '200gr TSX', weight: 200, bcG1: 0.494, bcG7: 0.252928 },
        { name: '200gr LRX', weight: 200, bcG1: 0.565, bcG7: 0.28928 }
      ],
      '.323': [
        { name: '160gr TSX', weight: 160, bcG1: 0.379, bcG7: 0.194048 },
        { name: '180gr TSX', weight: 180, bcG1: 0.482, bcG7: 0.246784 },
        { name: '208gr TSX', weight: 208, bcG1: 0.52, bcG7: 0.26624 }
      ],
      '.338': [
        { name: '210gr TSX', weight: 210, bcG1: 0.482, bcG7: 0.246784 },
        { name: '225gr TSX', weight: 225, bcG1: 0.514, bcG7: 0.263168 },
        { name: '250gr TSX', weight: 250, bcG1: 0.494, bcG7: 0.252928 },
        { name: '265gr LRX', weight: 265, bcG1: 0.618, bcG7: 0.316416 },
        { name: '280gr LRX', weight: 280, bcG1: 0.648, bcG7: 0.331776 },
        { name: '285gr TSX', weight: 285, bcG1: 0.6, bcG7: 0.3072 }
      ],
      '.358': [
        { name: '225gr TSX', weight: 225, bcG1: 0.473, bcG7: 0.242176 }
      ],
      '.375': [
        { name: '270gr TSX', weight: 270, bcG1: 0.398, bcG7: 0.203776 },
        { name: '250gr LRX', weight: 250, bcG1: 0.514, bcG7: 0.263168 },
        { name: '300gr TSX', weight: 300, bcG1: 0.482, bcG7: 0.246784 }
      ],
      '.416': [
        { name: '350gr TSX', weight: 350, bcG1: 0.38, bcG7: 0.19456 },
        { name: '400gr TSX', weight: 400, bcG1: 0.41, bcG7: 0.20992 }
      ],
      '.458': [
        { name: '(45-70) 250gr TSX', weight: 250, bcG1: 0.21, bcG7: 0.10752 },
        { name: '(45-70) 300gr TSX', weight: 300, bcG1: 0.248, bcG7: 0.126976 }
      ]
    },
    'Lapua': {
      '.224': [
        { name: '55gr Softpoint', weight: 55, bcG1: 0.255, bcG7: 0.13056 },
        { name: '69gr Scenar', weight: 69, bcG1: 0.318, bcG7: 0.162816 },
        { name: '69gr Scenar L', weight: 69, bcG1: 0.327, bcG7: 0.167424 }
      ],
      '.243': [
        { name: '90gr Scenar', weight: 90, bcG1: 0.355, bcG7: 0.18176 },
        { name: '105gr Scenar', weight: 105, bcG1: 0.536, bcG7: 0.274432 },
        { name: '105gr Scenar L', weight: 105, bcG1: 0.545, bcG7: 0.27904 }
      ],
      '.264': [
        { name: '100gr FMJ', weight: 100, bcG1: 0.25, bcG7: 0.128 },
        { name: '100gr Cutting Edge FMJ', weight: 100, bcG1: 0.26, bcG7: 0.13312 },
        { name: '100gr Scenar', weight: 100, bcG1: 0.45, bcG7: 0.2304 },
        { name: '108gr Scenar', weight: 108, bcG1: 0.465, bcG7: 0.23808 },
        { name: '120gr Scenar L', weight: 120, bcG1: 0.52, bcG7: 0.26624 },
        { name: '123gr Scenar', weight: 123, bcG1: 0.547, bcG7: 0.280064 },
        { name: '136gr Scenar L', weight: 136, bcG1: 0.615, bcG7: 0.31488 },
        { name: '139gr Scenar', weight: 139, bcG1: 0.615, bcG7: 0.31488 },
        { name: '140gr Naturalis', weight: 140, bcG1: 0.34, bcG7: 0.17408 },
        { name: '144gr FMJBT', weight: 144, bcG1: 0.365, bcG7: 0.18688 },
        { name: '155gr Mega', weight: 155, bcG1: 0.33, bcG7: 0.16896 }
      ],
      '.308': [
        { name: '100gr Cutting Edge OT', weight: 100, bcG1: 0.2, bcG7: 0.1024 },
        { name: '123gr FMJ', weight: 123, bcG1: 0.31, bcG7: 0.15872 },
        { name: '150gr Lockbase', weight: 150, bcG1: 0.33, bcG7: 0.16896 },
        { name: '150gr Mega', weight: 150, bcG1: 0.33, bcG7: 0.16896 },
        { name: '155gr Scenar', weight: 155, bcG1: 0.508, bcG7: 0.260096 },
        { name: '155gr Scenar L', weight: 155, bcG1: 0.512, bcG7: 0.262144 },
        { name: '167gr Scenar', weight: 167, bcG1: 0.47, bcG7: 0.24064 },
        { name: '170gr Lockbase', weight: 170, bcG1: 0.36, bcG7: 0.18432 },
        { name: '170gr Naturalis', weight: 170, bcG1: 0.41, bcG7: 0.20992 },
        { name: '175gr Scenar L', weight: 175, bcG1: 0.505, bcG7: 0.25856 },
        { name: '185gr Mega', weight: 185, bcG1: 0.37, bcG7: 0.18944 },
        { name: '185gr Scenar', weight: 185, bcG1: 0.485, bcG7: 0.24832 },
        { name: '200gr Mega', weight: 200, bcG1: 0.42, bcG7: 0.21504 },
        { name: '200gr Subsonic', weight: 200, bcG1: 0.43, bcG7: 0.22016 },
        { name: '220gr Scenar L', weight: 220, bcG1: 0.56, bcG7: 0.28672 }
      ],
      '.338': [
        { name: '250gr Lockbase', weight: 250, bcG1: 0.36, bcG7: 0.18432 },
        { name: '250gr Scenar', weight: 250, bcG1: 0.52, bcG7: 0.26624 }
      ],
      '.366': [
        { name: '250gr Naturalis', weight: 250, bcG1: 0.42, bcG7: 0.21504 },
        { name: '285gr Mega', weight: 285, bcG1: 0.48, bcG7: 0.24576 }
      ]
    },
    'Swift': {
      '.224': [
        { name: '75gr Scirocco', weight: 75, bcG1: 0.419, bcG7: 0.214528 }
      ],
      '.243': [
        { name: '90gr Scirocco', weight: 90, bcG1: 0.419, bcG7: 0.214528 }
      ],
      '.257': [
        { name: '100gr A-Frame', weight: 100, bcG1: 0.318, bcG7: 0.162816 }
      ],
      '.264': [
        { name: '120gr A-Frame', weight: 120, bcG1: 0.344, bcG7: 0.176128 },
        { name: '130gr Scirocco', weight: 130, bcG1: 0.571, bcG7: 0.292352 },
        { name: '140gr A-Frame', weight: 140, bcG1: 0.401, bcG7: 0.205312 }
      ],
      '.277': [
        { name: '130gr A-Frame', weight: 130, bcG1: 0.323, bcG7: 0.165376 },
        { name: '130gr Scirocco', weight: 130, bcG1: 0.45, bcG7: 0.2304 },
        { name: '140gr A-Frame', weight: 140, bcG1: 0.414, bcG7: 0.211968 }
      ],
      '.284': [
        { name: '140gr A-Frame', weight: 140, bcG1: 0.335, bcG7: 0.17152 },
        { name: '150gr Scirocco', weight: 150, bcG1: 0.515, bcG7: 0.26368 },
        { name: '160gr A-Frame', weight: 160, bcG1: 0.45, bcG7: 0.2304 },
        { name: '175gr A-Frame', weight: 175, bcG1: 0.493, bcG7: 0.252416 }
      ],
      '.308': [
        { name: '150gr Scirocco', weight: 150, bcG1: 0.43, bcG7: 0.22016 },
        { name: '165gr A-Frame', weight: 165, bcG1: 0.367, bcG7: 0.187904 },
        { name: '165gr Scirocco', weight: 165, bcG1: 0.47, bcG7: 0.24064 },
        { name: '180gr A-Frame', weight: 180, bcG1: 0.4, bcG7: 0.2048 },
        { name: '180gr Scirocco', weight: 180, bcG1: 0.52, bcG7: 0.26624 },
        { name: '200gr A-Frame', weight: 200, bcG1: 0.444, bcG7: 0.227328 }
      ],
      '.323': [
        { name: '200gr A-Frame', weight: 200, bcG1: 0.357, bcG7: 0.182784 },
        { name: '220gr A-Frame', weight: 220, bcG1: 0.393, bcG7: 0.201216 }
      ],
      '.338': [
        { name: '210gr Scirocco', weight: 210, bcG1: 0.507, bcG7: 0.259584 }
      ],
      '.358': [
        { name: '225gr A-Frame', weight: 225, bcG1: 0.312, bcG7: 0.159744 },
        { name: '250gr A-Frame', weight: 250, bcG1: 0.347, bcG7: 0.177664 },
        { name: '280gr A-Frame', weight: 280, bcG1: 0.388, bcG7: 0.198656 }
      ],
      '.366': [
        { name: '250gr A-Frame', weight: 250, bcG1: 0.285, bcG7: 0.14592 },
        { name: '300gr A-Frame', weight: 300, bcG1: 0.342, bcG7: 0.175104 }
      ],
      '.375': [
        { name: '250gr A-Frame', weight: 250, bcG1: 0.271, bcG7: 0.138752 },
        { name: '270gr A-Frame', weight: 270, bcG1: 0.349, bcG7: 0.178688 },
        { name: '300gr A-Frame', weight: 300, bcG1: 0.325, bcG7: 0.1664 }
      ],
      '.416': [
        { name: '350gr A-Frame', weight: 350, bcG1: 0.321, bcG7: 0.164352 },
        { name: '400gr A-Frame', weight: 400, bcG1: 0.367, bcG7: 0.187904 }
      ],
      '.423': [
        { name: '400gr A-Frame', weight: 400, bcG1: 0.375, bcG7: 0.192 }
      ],
      '.457': [
        { name: '(45-70) 400gr A-Frame', weight: 400, bcG1: 0.258, bcG7: 0.132096 },
        { name: '(45-70) 450gr A-Frame', weight: 450, bcG1: 0.325, bcG7: 0.1664 },
        { name: '(45-70) 500gr A-Frame', weight: 500, bcG1: 0.361, bcG7: 0.184832 }
      ],
      '.475': [
        { name: '500gr A-Frame', weight: 500, bcG1: 0.364, bcG7: 0.186368 }
      ]
    },
    'Speer': {
      '.224': [
        { name: '50gr TNT', weight: 50, bcG1: 0.228, bcG7: 0.116736 },
        { name: '55gr TNT', weight: 55, bcG1: 0.233, bcG7: 0.119296 }
      ],
      '.243': [
        { name: '70gr TNT SP', weight: 70, bcG1: 0.282, bcG7: 0.144384 },
        { name: '85gr Boat-Tail', weight: 85, bcG1: 0.38, bcG7: 0.19456 },
        { name: '90gr Hot-Cor', weight: 90, bcG1: 0.365, bcG7: 0.18688 },
        { name: '100gr Grand Slam SP', weight: 100, bcG1: 0.351, bcG7: 0.179712 },
        { name: '100gr Boat-Tail', weight: 100, bcG1: 0.446, bcG7: 0.228352 }
      ],
      '.257': [
        { name: '87gr Hot-Cor', weight: 87, bcG1: 0.3, bcG7: 0.1536 },
        { name: '87gr TNT', weight: 87, bcG1: 0.31, bcG7: 0.15872 },
        { name: '100gr Boat-Tail', weight: 100, bcG1: 0.393, bcG7: 0.201216 },
        { name: '100gr Hot-Cor', weight: 100, bcG1: 0.369, bcG7: 0.188928 },
        { name: '120gr Grand Slam SP', weight: 120, bcG1: 0.328, bcG7: 0.167936 },
        { name: '120gr Boat-Tail', weight: 120, bcG1: 0.48, bcG7: 0.24576 }
      ],
      '.264': [
        { name: '90gr TNT HP', weight: 90, bcG1: 0.281, bcG7: 0.143872 },
        { name: '140gr Grand Slam SP', weight: 140, bcG1: 0.498, bcG7: 0.254976 },
        { name: '140gr Hot-Cor', weight: 140, bcG1: 0.498, bcG7: 0.254976 },
        { name: '140gr Impact', weight: 140, bcG1: 0.526, bcG7: 0.273 }
      ],
      '.277': [
        { name: '90gr TNT', weight: 90, bcG1: 0.303, bcG7: 0.155136 },
        { name: '130gr Grand Slam SP', weight: 130, bcG1: 0.345, bcG7: 0.17664 },
        { name: '130gr Boat-Tail', weight: 130, bcG1: 0.412, bcG7: 0.210944 },
        { name: '130gr Hot-Cor', weight: 130, bcG1: 0.383, bcG7: 0.196096 },
        { name: '150gr Grand Slam SP', weight: 150, bcG1: 0.378, bcG7: 0.193536 },
        { name: '150gr Boat-Tail', weight: 150, bcG1: 0.496, bcG7: 0.253952 },
        { name: '150gr Hot-Cor', weight: 150, bcG1: 0.455, bcG7: 0.23296 }
      ],
      '.284': [
        { name: '130gr Hot-Cor', weight: 130, bcG1: 0.368, bcG7: 0.188416 },
        { name: '130gr Boat-Tail', weight: 130, bcG1: 0.384, bcG7: 0.196608 },
        { name: '145gr Grand Slam SP', weight: 145, bcG1: 0.353, bcG7: 0.180736 },
        { name: '145gr Boat-Tail', weight: 145, bcG1: 0.472, bcG7: 0.241664 },
        { name: '145gr Hot-Cor', weight: 145, bcG1: 0.416, bcG7: 0.212992 },
        { name: '160gr Grand Slam SP', weight: 160, bcG1: 0.389, bcG7: 0.199168 },
        { name: '160gr Boat-Tail', weight: 160, bcG1: 0.519, bcG7: 0.265728 },
        { name: '160gr Hot-Cor', weight: 160, bcG1: 0.504, bcG7: 0.258048 },
        { name: '175gr Grand Slam SP', weight: 175, bcG1: 0.436, bcG7: 0.223232 }
      ],
      '.308': [
        { name: '125gr TNT HP', weight: 125, bcG1: 0.341, bcG7: 0.174592 },
        { name: '150gr Hot-Cor SP', weight: 150, bcG1: 0.389, bcG7: 0.199168 },
        { name: '150gr Boat-Tail', weight: 150, bcG1: 0.417, bcG7: 0.213504 },
        { name: '165gr Grand Slam SP', weight: 165, bcG1: 0.393, bcG7: 0.201216 },
        { name: '165gr Boat-Tail', weight: 165, bcG1: 0.52, bcG7: 0.26624 },
        { name: '165gr Hot-Cor SP', weight: 165, bcG1: 0.433, bcG7: 0.221696 },
        { name: '172gr Impact', weight: 172, bcG1: 0.522, bcG7: 0.265 },
        { name: '180gr Grand Slam SP', weight: 180, bcG1: 0.374, bcG7: 0.191488 },
        { name: '180gr Boat-Tail', weight: 180, bcG1: 0.54, bcG7: 0.27648 },
        { name: '180gr Hot-Cor SP', weight: 180, bcG1: 0.441, bcG7: 0.225792 },
        { name: '190gr Impact', weight: 190, bcG1: 0.596, bcG7: 0.282 },
        { name: '200gr Hot-Cor SP', weight: 200, bcG1: 0.556, bcG7: 0.284672 }
      ],
      '.311': [
        { name: '150gr Hot-Cor', weight: 150, bcG1: 0.328, bcG7: 0.167936 },
        { name: '180gr Hot-Cor', weight: 180, bcG1: 0.328, bcG7: 0.167936 }
      ],
      '.323': [
        { name: '200gr Hot-Cor SP', weight: 200, bcG1: 0.411, bcG7: 0.210432 }
      ]
    }
  };

  const updateFieldDataPoint = (id, field, value) => {
    setFieldDataPoints(pts => pts.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const addFieldDataPoint = () => {
    setFieldDataPoints(pts => [...pts, { id: Date.now(), target: '', clicks: '', adjustment: '' }]);
  };

  const removeFieldDataPoint = (id) => {
    setFieldDataPoints(pts => pts.filter(p => p.id !== id));
  };

  // --- Atmospheric model (ICAO imperial, matches bccalculator.com constants) ---
  // Station pressure/temperature from altitude (bccalculator exact constants).
  const ATMOS = { P0: 29.92, G: 32.174, B: 0.00357, R: 1716, TR: 518.67, Tdiff: 459.67 };

  // ICAO sea-level dry-air density (kg/m^3). Bullet BCs are referenced to this.
  const RHO_STD = 1.225;

  // Humid-air density (kg/m^3) from station temp (°F), pressure (inHg), humidity (%).
  // Uses ideal-gas law with Tetens saturation-vapor pressure.
  const calculateAirDensity = (tempF, pressureInHg, humidityPct) => {
    const tempC = (tempF - 32) * 5 / 9;
    const tempK = tempC + 273.15;
    const pressurePa = pressureInHg * 3386.389;
    // Tetens: saturation vapor pressure over water (Pa)
    const pSat = 610.78 * Math.exp((17.27 * tempC) / (tempC + 237.3));
    const pV = (humidityPct / 100) * pSat;           // partial pressure of water vapor
    const pD = pressurePa - pV;                       // partial pressure of dry air
    // Specific gas constants: dry air 287.058, water vapor 461.495 (J/(kg·K))
    return pD / (287.058 * tempK) + pV / (461.495 * tempK);
  };

  // Speed of sound (m/s) in humid air (small humidity effect folded in via density ratio)
  const speedOfSound = (tempF) => {
    const tempK = (tempF - 32) * 5 / 9 + 273.15;
    return Math.sqrt(1.4 * 287.058 * tempK);
  };

  // --- Standard drag tables: Cd as a function of Mach number (ICAO reference) ---
  // Values from the McCoy / JBM published G1 and G7 standard projectiles.
  const G1_TABLE = [
    [0.00,0.2629],[0.05,0.2558],[0.10,0.2487],[0.15,0.2413],[0.20,0.2344],
    [0.25,0.2278],[0.30,0.2214],[0.35,0.2155],[0.40,0.2104],[0.45,0.2061],
    [0.50,0.2032],[0.55,0.2020],[0.60,0.2034],[0.70,0.2165],[0.725,0.2230],
    [0.75,0.2313],[0.775,0.2417],[0.80,0.2546],[0.825,0.2706],[0.85,0.2901],
    [0.875,0.3136],[0.90,0.3415],[0.925,0.3734],[0.95,0.4084],[0.975,0.4448],
    [1.00,0.4805],[1.025,0.5136],[1.05,0.5427],[1.075,0.5677],[1.10,0.5883],
    [1.125,0.6053],[1.15,0.6191],[1.20,0.6393],[1.25,0.6518],[1.30,0.6589],
    [1.35,0.6621],[1.40,0.6625],[1.45,0.6607],[1.50,0.6573],[1.55,0.6528],
    [1.60,0.6474],[1.65,0.6413],[1.70,0.6347],[1.75,0.6280],[1.80,0.6210],
    [1.85,0.6141],[1.90,0.6072],[1.95,0.6003],[2.00,0.5934],[2.05,0.5867],
    [2.10,0.5804],[2.20,0.5683],[2.30,0.5573],[2.40,0.5473],[2.50,0.5382],
    [2.60,0.5300],[2.70,0.5226],[2.80,0.5158],[2.90,0.5094],[3.00,0.5033],
    [3.10,0.4974],[3.20,0.4917],[3.40,0.4807],[3.60,0.4701],[3.80,0.4598],
    [4.00,0.4498],[4.40,0.4308],[4.80,0.4137],[5.00,0.4057]
  ];
  const G7_TABLE = [
    [0.00,0.1198],[0.05,0.1197],[0.40,0.1197],[0.50,0.1194],[0.60,0.1194],
    [0.70,0.1194],[0.725,0.1195],[0.75,0.1195],[0.775,0.1200],[0.80,0.1204],
    [0.825,0.1210],[0.85,0.1223],[0.875,0.1242],[0.90,0.1278],[0.925,0.1348],
    [0.95,0.1476],[0.975,0.1730],[1.00,0.2144],[1.025,0.2505],[1.05,0.2617],
    [1.075,0.2633],[1.10,0.2623],[1.125,0.2604],[1.15,0.2580],[1.20,0.2519],
    [1.25,0.2458],[1.30,0.2402],[1.35,0.2351],[1.40,0.2305],[1.45,0.2266],
    [1.50,0.2229],[1.55,0.2196],[1.60,0.2167],[1.65,0.2141],[1.70,0.2117],
    [1.75,0.2095],[1.80,0.2075],[1.85,0.2057],[1.90,0.2039],[1.95,0.2023],
    [2.00,0.2008],[2.05,0.1993],[2.10,0.1979],[2.20,0.1956],[2.30,0.1934],
    [2.40,0.1915],[2.50,0.1896]
  ];

  const cdAt = (mach, model) => {
    const t = model === 'G7' ? G7_TABLE : G1_TABLE;
    if (mach <= t[0][0]) return t[0][1];
    if (mach >= t[t.length - 1][0]) return t[t.length - 1][1];
    for (let i = 1; i < t.length; i++) {
      if (mach <= t[i][0]) {
        const [m0, c0] = t[i - 1];
        const [m1, c1] = t[i];
        return c0 + (c1 - c0) * (mach - m0) / (m1 - m0);
      }
    }
    return t[t.length - 1][1];
  };

  // Drag deceleration magnitude (m/s^2) for an actual projectile.
  // Derivation: for the G1/G7 reference projectile (SD = 1 lb/in^2, form factor i = 1),
  // a = 0.5 * rho * V^2 * Cd * A / m, with A/m = pi*d^2/(4*m).
  // Reference SD=1 lb/in^2 gives A/m = (pi/4) * (0.0254^2 m^2) / (0.4536 kg) = 1.1168e-3 m^2/kg.
  // Thus a_ref = 0.5 * rho * V^2 * Cd * 1.1168e-3.  Actual: divide by BC.
  const DRAG_K_SI = 0.5 * 1.1168e-3; // 5.584e-4

  const dragDecel = (vMs, cd, rho, bc) => DRAG_K_SI * rho * vMs * vMs * cd / bc;

  // Point-mass integrator. Inputs in SI. Returns samples at requested ranges (m).
  const simulate = ({ v0, angle, scopeH, rho, vs, bc, model, gEff, crosswindMs, sampleRanges }) => {
    let x = 0, y = -scopeH;          // bullet starts at scope-height below LOS
    let vx = v0 * Math.cos(angle);
    let vy = v0 * Math.sin(angle);
    let t = 0;
    const dt = 0.0005;                // 0.5 ms — ~0.1 yd at typical velocities
    const samples = [];
    let nextIdx = 0;
    const maxT = 10;                  // safety

    const step = () => {
      const v = Math.sqrt(vx * vx + vy * vy);
      const cd = cdAt(v / vs, model);
      const aDrag = dragDecel(v, cd, rho, bc);
      // drag opposes velocity vector
      const ax = -aDrag * (vx / v);
      const ay = -aDrag * (vy / v) - gEff;
      // RK2 (midpoint) for better accuracy
      const vxMid = vx + ax * dt * 0.5;
      const vyMid = vy + ay * dt * 0.5;
      const vMid = Math.sqrt(vxMid * vxMid + vyMid * vyMid);
      const cdMid = cdAt(vMid / vs, model);
      const aMid = dragDecel(vMid, cdMid, rho, bc);
      const axM = -aMid * (vxMid / vMid);
      const ayM = -aMid * (vyMid / vMid) - gEff;
      x += vxMid * dt;
      y += vyMid * dt;
      vx += axM * dt;
      vy += ayM * dt;
      t += dt;
    };

    while (nextIdx < sampleRanges.length && t < maxT) {
      const target = sampleRanges[nextIdx];
      const xPrev = x, yPrev = y, tPrev = t, vxPrev = vx, vyPrev = vy;
      step();
      while (x < target && t < maxT) {
        step();
      }
      // linear interpolate back to exact range
      const frac = (target - xPrev) / (x - xPrev || 1);
      const xi = target;
      const yi = yPrev + (y - yPrev) * frac;
      const ti = tPrev + (t - tPrev) * frac;
      const vxi = vxPrev + (vx - vxPrev) * frac;
      const vyi = vyPrev + (vy - vyPrev) * frac;
      const vi = Math.sqrt(vxi * vxi + vyi * vyi);
      // Didion crosswind deflection: W * (t - x/V0_x)
      const windDeflMs = crosswindMs * (ti - xi / (v0 * Math.cos(angle)));
      samples.push({ x: xi, y: yi, v: vi, t: ti, wind: windDeflMs });
      nextIdx++;
    }
    return samples;
  };

  // Solve launch angle so the bullet crosses LOS (y=0) at the zero range.
  const solveZeroAngle = (ctx, zeroRangeM) => {
    let lo = -0.02, hi = 0.05;        // radians (~-1.1° to +2.9°)
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      const s = simulate({ ...ctx, angle: mid, sampleRanges: [zeroRangeM] });
      const y = s.length ? s[0].y : -Infinity;
      if (y > 0) hi = mid; else lo = mid;
    }
    return (lo + hi) / 2;
  };

  // TrueBC solver: given muzzle velocity + field-shooting data points (range,
  // observed-drop), fit the BC that minimizes the sum of squared drop residuals.
  // Uses coarse-to-fine bracketing over BC in [0.05, 1.5]. Zero angle is
  // re-solved for each candidate BC so the fit accounts for sight-in geometry.
  const solveBcFromFieldData = (ctxBase, zeroRangeM, fieldPointsSI) => {
    // fieldPointsSI: [{ rangeM, dropM }] — dropM positive = below line-of-sight
    if (!fieldPointsSI.length) return null;

    const evalResidual = (bc) => {
      const ctx = { ...ctxBase, bc };
      const angle = solveZeroAngle(ctx, zeroRangeM);
      const ranges = fieldPointsSI.map(p => p.rangeM);
      const samples = simulate({ ...ctx, angle, sampleRanges: ranges });
      if (samples.length !== fieldPointsSI.length) return Infinity;
      let sse = 0;
      for (let i = 0; i < samples.length; i++) {
        const predDropM = -samples[i].y;          // positive = below LOS
        const resid = predDropM - fieldPointsSI[i].dropM;
        sse += resid * resid;
      }
      return sse;
    };

    const search = (lo, hi, step) => {
      let bestBc = lo, bestErr = Infinity;
      for (let bc = lo; bc <= hi + 1e-9; bc += step) {
        const err = evalResidual(bc);
        if (err < bestErr) { bestErr = err; bestBc = bc; }
      }
      return bestBc;
    };

    let best = search(0.10, 1.50, 0.05);          // stage 1: coarse
    best = search(Math.max(0.05, best - 0.05), Math.min(1.5, best + 0.05), 0.01); // stage 2
    best = search(Math.max(0.05, best - 0.01), Math.min(1.5, best + 0.01), 0.001); // stage 3
    return best;
  };

  const calculateTrajectory = () => {
    // Validate before running: cleared inputs ('') would flow through the
    // math as NaN, and a zero/negative range step would loop forever below.
    const num = (v) => (v === '' || v === null ? NaN : Number(v));
    const problems = [];
    if (!(num(formData.muzzleVelocity) > 0)) problems.push('Muzzle Velocity');
    if (formData.useBC && !(num(formData.ballisticCoefficient) > 0)) problems.push('Ballistic Coefficient');
    if (!(num(formData.bulletWeight) > 0)) problems.push('Bullet Weight');
    if (!Number.isFinite(num(formData.scopeHeight))) problems.push('Scope Height');
    if (!(num(formData.zeroRange) > 0)) problems.push('Zero Range');
    if (!Number.isFinite(num(formData.temperature))) problems.push('Temperature');
    if (!(num(formData.pressure) > 0)) problems.push('Pressure');
    if (!Number.isFinite(num(formData.humidity))) problems.push('Humidity');
    if (!Number.isFinite(num(formData.windSpeed))) problems.push('Wind Speed');
    if (!Number.isFinite(num(formData.startRange)) || num(formData.startRange) < 0) problems.push('Range Start');
    if (!Number.isFinite(num(formData.endRange)) || num(formData.endRange) < num(formData.startRange)) problems.push('Range End');
    if (!(num(formData.rangeIncrement) > 0)) problems.push('Range Step');
    if (problems.length) {
      alert(`Please enter a valid value for: ${problems.join(', ')}`);
      return;
    }

    setIsCalculating(true);
    setTimeout(() => {
      // Unit conversions (user inputs are imperial unless noted)
      const FT_PER_M = 3.28084;
      const IN_PER_M = 39.3701;
      const M_PER_YD = 0.9144;
      const MPS_PER_FPS = 0.3048;
      const MPH_TO_MPS = 0.44704;

      const v0 = formData.muzzleVelocity * MPS_PER_FPS;                 // m/s
      const model = formData.dragModel;                                  // 'G1' | 'G7'
      const rho = calculateAirDensity(formData.temperature, formData.pressure, formData.humidity);
      const vs = speedOfSound(formData.temperature);
      const scopeH = formData.scopeHeight * 0.0254;                      // m
      const zeroM = formData.zeroRange * M_PER_YD;
      const inclRad = (formData.inclination || 0) * Math.PI / 180;
      const gEff = 9.80665 * Math.cos(inclRad);                          // effective gravity along LOS-perp
      const crosswindMs = formData.windSpeed * MPH_TO_MPS;               // full crosswind (3/9 o'clock)

      // BC: either user-entered ("Use BC") or solved from field data ("Use Velocity").
      let bc = formData.ballisticCoefficient;                            // lb/in^2
      let solvedBc = null;
      if (!formData.useBC) {
        // Legacy uses Target + Clicks + Adjustment. Drop below LOS in inches is:
        //   dropIn = clicks * clickValueMOA * (targetYds / 100) * 1.047 + adjustmentIn
        const clickValueMOA = Number(formData.clickValue) || 0;
        const validPoints = fieldDataPoints
          .filter(p => Number(p.target) > 0)
          .map(p => {
            const targetYds = Number(p.target);
            const clicks = Number(p.clicks) || 0;
            const adjIn = Number(p.adjustment) || 0;
            const dropIn = clicks * clickValueMOA * (targetYds / 100) * 1.047 + adjIn;
            return { rangeM: targetYds * M_PER_YD, dropM: dropIn * 0.0254 };
          });
        if (validPoints.length === 0) {
          setIsCalculating(false);
          alert('"Use Velocity" mode requires at least one Field Shooting Data point with a Target range.');
          return;
        }
        const fitCtx = { v0, scopeH, rho, vs, model, gEff, crosswindMs };
        solvedBc = solveBcFromFieldData(fitCtx, zeroM, validPoints);
        if (solvedBc != null) bc = solvedBc;
      }

      const baseCtx = { v0, scopeH, rho, vs, bc, model, gEff, crosswindMs };
      const angle = solveZeroAngle(baseCtx, zeroM);

      const ranges = [];
      // Validation guarantees a positive step; the row cap is a backstop so a
      // tiny step can't build an unbounded table.
      for (let r = formData.startRange; r <= formData.endRange && ranges.length < 2000; r += formData.rangeIncrement) {
        ranges.push(r);
      }
      const sampleRanges = ranges.map(r => r * M_PER_YD);
      const samples = simulate({ ...baseCtx, angle, sampleRanges });

      // --- Alternate Conditions: re-simulate with overridden incline or atmosphere.
      // Returns { type, showVariation, clicks[] } per configured column.
      const clickValueMOA = Number(formData.clickValue) || 0.25;
      const alternateResults = alternateColumns.map(col => {
        if (col.type === 'None') return null;
        let altCtx = { ...baseCtx };
        if (col.type === 'Incline') {
          const altIncRad = (Number(col.incline) || 0) * Math.PI / 180;
          altCtx.gEff = 9.80665 * Math.cos(altIncRad);
        } else if (col.type === 'Air Density') {
          const altPressureInHg = pressureFromAltitude(Number(col.elevation) || 0);
          altCtx.rho = calculateAirDensity(Number(col.temperature) || 59, altPressureInHg, formData.humidity);
          altCtx.vs = speedOfSound(Number(col.temperature) || 59);
        }
        const altAngle = solveZeroAngle(altCtx, zeroM);
        const altSamples = simulate({ ...altCtx, angle: altAngle, sampleRanges });
        const clicks = altSamples.map((s, i) => {
          const rangeYds = ranges[i];
          const dropIn = -s.y * IN_PER_M;
          const moaPerIn = rangeYds > 0 ? 100 / (rangeYds * 1.047) : 0;
          const dropMOA = dropIn * moaPerIn;
          return Math.round((dropMOA / clickValueMOA) * 10) / 10;
        });
        return { type: col.type, showVariation: !!col.showVariation, clicks };
      });

      const trajectoryData = samples.map((s, i) => {
        const range = ranges[i];                                         // yards
        // Drop = drop below LOS in inches (positive number means bullet below LOS)
        const dropIn = -s.y * IN_PER_M;
        const windIn = s.wind * IN_PER_M;
        const velocityFps = s.v / MPS_PER_FPS;
        // Energy (ft-lb) = m(grains) * v(fps)^2 / 450437  (using g=32.174)
        const energy = (formData.bulletWeight * velocityFps * velocityFps) / 450437;
        // 1 MOA at 100 yds = 1.047"; 1 MIL at 100 yds = 3.6"
        const moaPerIn = range > 0 ? 100 / (range * 1.047) : 0;
        const milPerIn = range > 0 ? 100 / (range * 3.6) : 0;
        const dropMOA = dropIn * moaPerIn;
        const dropMRad = dropIn * milPerIn;
        const windMOA = windIn * moaPerIn;
        const windMRad = windIn * milPerIn;

        return {
          range,
          velocity: Math.round(velocityFps),
          energy: Math.round(energy),
          drop: dropIn.toFixed(1),
          dropMOA: dropMOA.toFixed(2),
          dropMRad: dropMRad.toFixed(2),
          dropClicks: Math.round(dropMOA / formData.clickValue),
          windDrift: windIn.toFixed(1),
          windMOA: windMOA.toFixed(2),
          windMRad: windMRad.toFixed(2),
          windClicks: Math.round(windMOA / formData.clickValue),
          timeOfFlight: s.t.toFixed(3)
        };
      });

      // Push the fitted BC back into the form so the UI reflects it and
      // subsequent "Use BC" calculations pick up from the solved value.
      if (solvedBc != null) {
        setFormData(prev => ({ ...prev, ballisticCoefficient: +solvedBc.toFixed(3) }));
      }

      setResults({
        trajectoryData,
        trueBc: bc.toFixed(3),
        trueMuzzleVelocity: formData.muzzleVelocity,
        alternates: alternateResults
      });
      setIsCalculating(false);
      requestAnimationFrame(() => {
        const el = document.querySelector('.results-container');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }, 50);
  };

  // Unit conversion helpers - internal state is kept in imperial units.
  const unitLabels = {
    range: formData.unitSystem === 'imperial' ? 'yds' : 'm',
    velocity: formData.unitSystem === 'imperial' ? 'fps' : 'm/s',
    energy: formData.unitSystem === 'imperial' ? 'ft-lb' : 'J',
    drop: formData.unitSystem === 'imperial' ? 'in' : 'mm',
    windDrift: formData.unitSystem === 'imperial' ? 'in' : 'mm',
    pressure: formData.unitSystem === 'imperial' ? 'inHg' : 'kPa',
    temp: formData.unitSystem === 'imperial' ? '\u00b0F' : '\u00b0C',
    windSpeed: formData.unitSystem === 'imperial' ? 'mph' : 'km/h',
    scopeHeight: formData.unitSystem === 'imperial' ? 'in' : 'mm',
    elevation: formData.unitSystem === 'imperial' ? 'ft' : 'm',
    bulletWeight: 'gr',
    zeroAdjustment: formData.unitSystem === 'imperial' ? 'in' : 'mm'
  };

  const displayValue = (value, kind) => {
    if (value === '' || value === undefined || value === null) return '';
    const v = Number(value);
    if (Number.isNaN(v)) return value;
    if (formData.unitSystem === 'imperial') return v;

    // Metric conversions
    switch (kind) {
      case 'elevation': return +(v * 0.3048).toFixed(1); // ft -> m
      case 'temperature': return +(((v - 32) * 5 / 9).toFixed(1)); // F -> C
      case 'pressure': return +(v * 3.386389).toFixed(2); // inHg -> kPa
      case 'windSpeed': return +(v * 1.609344).toFixed(2); // mph -> km/h
      case 'scopeHeight': return +(v * 25.4).toFixed(1); // in -> mm
      case 'zeroRange':
      case 'startRange':
      case 'endRange':
      case 'rangeIncrement': return +(v * 0.9144).toFixed(1); // yds -> m
      case 'muzzleVelocity': return +(v * 0.3048).toFixed(1); // fps -> m/s
      case 'bulletWeight': return v; // grains (no conversion — legacy keeps grains in metric)
      case 'zeroAdjustment': return +(v * 25.4).toFixed(1); // in -> mm
      case 'drop': return +(v * 25.4).toFixed(1); // in -> mm
      case 'windDrift': return +(v * 25.4).toFixed(1); // in -> mm
      case 'velocity': return +(v * 0.3048).toFixed(1); // fps -> m/s (used for results/field)
      case 'energy': return +(v * 1.355817948).toFixed(0); // ft-lb -> J
      default: return v;
    }
  };

  const parseInput = (inputValue, kind) => {
    const v = Number(inputValue);
    if (inputValue === '') return '';
    if (Number.isNaN(v)) return inputValue;
    if (formData.unitSystem === 'imperial') return v;

    // Convert metric user input back to imperial for internal storage
    switch (kind) {
      case 'elevation': return +(v / 0.3048); // m -> ft
      case 'temperature': return +(v * 9 / 5 + 32); // C -> F
      case 'pressure': return +(v / 3.386389); // kPa -> inHg
      case 'windSpeed': return +(v / 1.609344); // km/h -> mph
      case 'scopeHeight': return +(v / 25.4); // mm -> in
      case 'zeroRange':
      case 'startRange':
      case 'endRange':
      case 'rangeIncrement': return +(v / 0.9144); // m -> yds
      case 'muzzleVelocity': return +(v / 0.3048); // m/s -> fps
      case 'bulletWeight': return v; // grains (no conversion)
      case 'zeroAdjustment': return +(v / 25.4); // mm -> in
      case 'drop': return +(v / 25.4); // mm -> in
      case 'windDrift': return +(v / 25.4); // mm -> in
      case 'velocity': return +(v / 0.3048); // m/s -> fps
      case 'energy': return +(v / 1.355817948); // J -> ft-lb
      default: return v;
    }
  };

  const formatRowForDisplay = (row) => {
    return {
      range: displayValue(row.range, 'startRange'),
      velocity: displayValue(row.velocity, 'velocity'),
      energy: displayValue(row.energy, 'energy'),
      drop: displayValue(row.drop, 'drop'),
      dropMOA: row.dropMOA,
      dropMRad: row.dropMRad,
      dropClicks: row.dropClicks,
      windDrift: displayValue(row.windDrift, 'windDrift'),
      windMOA: row.windMOA,
      windMRad: row.windMRad,
      timeOfFlight: row.timeOfFlight
    };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Changing elevation auto-derives station pressure (ICAO), matching the
  // legacy calculator. Pressure remains directly editable as a manual override.
  const handleElevationChange = (value) => {
    const altFt = Number(value);
    setFormData(prev => ({
      ...prev,
      elevation: value,
      ...(value !== '' && !Number.isNaN(altFt)
        ? { pressure: +pressureFromAltitude(altFt).toFixed(2) }
        : {})
    }));
  };

  // Pick the correct BC for the currently-selected drag model. Bullets can carry
  // bcG1, bcG7, or legacy `bc` — prefer the model-matching value, then any available.
  const getBulletBC = (bullet, model) => {
    if (!bullet) return null;
    if (model === 'G7' && bullet.bcG7 != null) return bullet.bcG7;
    if (model === 'G1' && bullet.bcG1 != null) return bullet.bcG1;
    return bullet.bcG1 ?? bullet.bcG7 ?? bullet.bc ?? null;
  };

  const handleBulletSelection = (company, caliber, bullet) => {
    if (bullet) {
      // If the selected bullet only has a G7 BC (e.g. Berger/Lapua long-range),
      // auto-switch to G7 so the displayed BC matches the drag model.
      const nextModel =
        bullet.bcG7 != null && bullet.bcG1 == null ? 'G7' :
        bullet.bcG1 != null && bullet.bcG7 == null ? 'G1' :
        formData.dragModel;
      setFormData(prev => ({
        ...prev,
        bulletCompany: company,
        bulletCaliber: caliber,
        bulletType: bullet.name,
        bulletWeight: bullet.weight,
        dragModel: nextModel,
        ballisticCoefficient: getBulletBC(bullet, nextModel) ?? bullet.bc ?? prev.ballisticCoefficient
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      unitSystem: 'imperial',
      dragModel: 'G1',
      gameSize: 'none',
      bulletCompany: '',
      bulletCaliber: '',
      bulletType: '',
      bulletDescription: '',
      bulletWeight: 180,
      elevation: 0,
      temperature: 59,
      pressure: 29.92,
      humidity: 50,
      windSpeed: 10,
      scopeHeight: 1.75,
      zeroRange: 100,
      zeroAdjustment: 0,
      inclination: 0,
      clickValue: 0.25,
      useBC: true,
      ballisticCoefficient: 0.5,
      muzzleVelocity: 2800,
      startRange: 100,
      endRange: 1500,
      rangeIncrement: 50
    });
    setResults(null);
    setFieldDataPoints([{ id: 'primary', target: '', clicks: '', adjustment: '' }]);
    setAlternateColumns([
      { type: 'None', incline: 0, elevation: 0, temperature: 59, showVariation: true },
      { type: 'None', incline: 0, elevation: 0, temperature: 59, showVariation: true }
    ]);
  };

  const exportData = () => {
    if (!results) return;
    const headers = [
      `Range (${unitLabels.range})`,
      `Velocity (${unitLabels.velocity})`,
      `Energy (${unitLabels.energy})`,
      `Drop (${unitLabels.drop})`,
      'Drop (MOA)',
      'Drop (MRad)',
      'Drop (clicks)',
      `Wind Drift (${unitLabels.windDrift})`,
      'Wind (MOA)',
      'Wind (MRad)',
      'Wind (clicks)'
    ];

    const rows = results.trajectoryData.map(r => {
      const d = formatRowForDisplay(r);
      return [d.range, d.velocity, d.energy, d.drop, d.dropMOA, d.dropMRad, d.dropClicks, d.windDrift, d.windMOA, d.windMRad, d.windClicks];
    });

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ballistics_data.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printRangeCard = () => {
    if (!results) return;

    // Create print-specific content
    const printWindow = window.open('', '_blank');
    const bulletInfo = `${formData.bulletCompany} ${formData.bulletCaliber} ${formData.bulletType} - ${formData.bulletWeight}gr`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Range Card - ${bulletInfo}</title>
          <style>
            @page { margin: 0.4in; size: letter; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              background: white;
              color: black;
              font-size: 10px;
              line-height: 1.2;
            }
            .range-card {
              width: 100%;
              height: 100vh;
              display: flex;
              flex-direction: column;
            }
            .header {
              text-align: center;
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 2px solid black;
            }
            .header h1 {
              font-size: 16px;
              margin-bottom: 4px;
              font-weight: bold;
            }
            .header p {
              font-size: 9px;
            }
            .data-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
              margin-bottom: 12px;
              padding: 8px;
              border: 1px solid black;
              background: #f5f5f5;
            }
            .data-column h3 {
              font-size: 11px;
              margin-bottom: 6px;
              font-weight: bold;
              text-decoration: underline;
            }
            .data-item {
              margin-bottom: 3px;
              font-size: 9px;
              display: flex;
            }
            .data-item strong {
              width: 80px;
              flex-shrink: 0;
            }
            .trajectory-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8px;
              flex: 1;
              margin-bottom: 10px;
            }
            .trajectory-table th,
            .trajectory-table td {
              border: 1px solid black;
              padding: 2px 3px;
              text-align: center;
              line-height: 1.1;
            }
            .trajectory-table th {
              background: #e0e0e0;
              font-weight: bold;
              font-size: 7px;
              padding: 3px 2px;
            }
            .trajectory-table tr:nth-child(even) {
              background: #f9f9f9;
            }
            .range-col {
              font-weight: bold;
              background: #f0f0f0 !important;
              width: 40px;
            }
            .field-data {
              padding: 8px;
              border: 1px solid black;
              background: #f5f5f5;
              margin-top: auto;
            }
            .field-data h3 {
              font-size: 10px;
              margin-bottom: 8px;
              font-weight: bold;
              text-align: center;
            }
            .field-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              font-size: 8px;
            }
            .field-item {
              display: flex;
              margin-bottom: 4px;
              align-items: center;
            }
            .field-item strong {
              width: 70px;
              flex-shrink: 0;
            }
            .field-line {
              flex: 1;
              border-bottom: 1px solid black;
              margin-left: 6px;
              height: 12px;
            }
            /* Ensure compact table rows */
            tbody tr {
              height: 16px;
            }
          </style>
        </head>
        <body>
          <div class="range-card">
            <div class="header">
              <h1>Range Card / Data Collection Sheet</h1>
              <p>Generated: ${new Date().toLocaleDateString()} | Enhanced Ballistics Calculator</p>
            </div>
            
            <div class="data-section">
              <div class="data-column">
                <h3>Bullet Description</h3>
                <div class="data-item"><strong>Bullet:</strong> <span>${bulletInfo}</span></div>
                <div class="data-item"><strong>BC:</strong> <span>${formData.ballisticCoefficient}</span></div>
                <div class="data-item"><strong>Muzzle V:</strong> <span>${formData.muzzleVelocity} fps</span></div>
                <div class="data-item"><strong>Sight Ht:</strong> <span>${formData.scopeHeight} in</span></div>
                <div class="data-item"><strong>Zero:</strong> <span>${formData.zeroRange} yds</span></div>
                <div class="data-item"><strong>Click:</strong> <span>${formData.clickValue} MOA</span></div>
              </div>
              <div class="data-column">
                <h3>Environmental Data</h3>
                <div class="data-item"><strong>Altitude:</strong> <span>${formData.elevation} ft</span></div>
                <div class="data-item"><strong>Temp:</strong> <span>${formData.temperature}°F</span></div>
                <div class="data-item"><strong>Pressure:</strong> <span>${formData.pressure} inHg</span></div>
                <div class="data-item"><strong>Humidity:</strong> <span>${formData.humidity}%</span></div>
                <div class="data-item"><strong>Wind:</strong> <span>${formData.windSpeed} mph</span></div>
                <div class="data-item"><strong>Drag:</strong> <span>${formData.dragModel}</span></div>
              </div>
            </div>
            
            <table class="trajectory-table">
              <thead>
                <tr>
                  <th>Range<br>(Yds)</th>
                  <th>Vel<br>(fps)</th>
                  <th>Energy<br>(ft-lb)</th>
                  <th>Wind<br>(MOA)</th>
                  <th>Wind<br>(MRad)</th>
                  <th>Drop<br>(Clks)</th>
                  <th>Drop<br>(MOA)</th>
                  <th>Drop<br>(MRad)</th>
                  <th>Drop<br>(in)</th>
                </tr>
              </thead>
              <tbody>
                ${results.trajectoryData.map(row => `
                  <tr>
                    <td class="range-col">${row.range}</td>
                    <td>${row.velocity}</td>
                    <td>${row.energy}</td>
                    <td>${row.windMOA}</td>
                    <td>${row.windMRad}</td>
                    <td>${row.dropClicks}</td>
                    <td>${row.dropMOA}</td>
                    <td>${row.dropMRad}</td>
                    <td>${row.drop}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="field-data">
              <h3>Field Data Collection</h3>
              <div class="field-grid">
                <div>
                  <div class="field-item">
                    <strong>Velocity:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Altitude:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Temp:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Pressure:</strong>
                    <div class="field-line"></div>
                  </div>
                </div>
                <div>
                  <div class="field-item">
                    <strong>Humidity:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Range:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Clicks:</strong>
                    <div class="field-line"></div>
                  </div>
                  <div class="field-item">
                    <strong>Drop:</strong>
                    <div class="field-line"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const startTutorial = () => {
    setShowTutorial(true);
    setTutorialStep(0);
  };

  const nextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      closeTutorial();
    }
  };

  const prevTutorialStep = () => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialStep(0);
  };

  const goToTutorialStep = (step) => {
    setTutorialStep(step);
    setShowTutorial(true);
  };

  return (
    <div className="ballistics-calculator">
      <div className="calculator-container">
        <div className="header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="title">TrueBC®</h1>
              <p className="subtitle">Enhanced Ballistics Calculator</p>
            </div>
            <div className="header-right">
              <div className="header-controls">
                <div className="toggle-container">
                  <label className="toggle-label">
                    <span className="toggle-text">Field Data</span>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={showFieldData}
                        onChange={(e) => setShowFieldData(e.target.checked)}
                        className="toggle-input"
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                </div>
                <button onClick={startTutorial} className="btn btn-header">
                  <HelpCircle size={16} />
                  Tutorial
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Action Bar */}
        <div className="sticky-action-bar">
          <div className="sticky-actions">
            <button
              onClick={calculateTrajectory}
              disabled={isCalculating}
              className="btn btn-primary btn-large"
            >
              <Calculator size={18} />
              {isCalculating ? 'Calculating...' : 'Calculate'}
            </button>

            <button onClick={resetForm} className="btn btn-secondary">
              <RotateCcw size={18} />
              Reset
            </button>

            {results && (
              <button onClick={exportData} className="btn btn-success">
                <Download size={18} />
                Export CSV
              </button>
            )}

            {results && (
              <button onClick={printRangeCard} className="btn btn-success">
                <Download size={18} />
                Print Range Card
              </button>
            )}

            {results && (
              <button
                onClick={() => window.open('https://huskemawoptics.com/collections/turrets', '_blank')}
                className="btn btn-turret"
              >
                <Target size={18} />
                Order Turret
              </button>
            )}

          </div>
        </div>

        <div className="grid-container">
          {/* Measurement Options */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Info size={16} className="card-title-icon" />
                Units &amp; Drag
              </h3>
              <button
                onClick={() => goToTutorialStep(1)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Unit System</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="unitSystem"
                    value="imperial"
                    checked={formData.unitSystem === 'imperial'}
                    onChange={(e) => handleInputChange('unitSystem', e.target.value)}
                    className="radio-input"
                  />
                  Imperial
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="unitSystem"
                    value="metric"
                    checked={formData.unitSystem === 'metric'}
                    onChange={(e) => handleInputChange('unitSystem', e.target.value)}
                    className="radio-input"
                  />
                  Metric
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Drag Model</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="dragModel"
                    value="G1"
                    checked={formData.dragModel === 'G1'}
                    onChange={(e) => handleInputChange('dragModel', e.target.value)}
                    className="radio-input"
                  />
                  G1
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="dragModel"
                    value="G7"
                    checked={formData.dragModel === 'G7'}
                    onChange={(e) => handleInputChange('dragModel', e.target.value)}
                    className="radio-input"
                  />
                  G7
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Game Size</label>
              <select
                value={formData.gameSize}
                onChange={(e) => handleInputChange('gameSize', e.target.value)}
                className="form-select"
              >
                <option value="none">Select game size</option>
                <option value="small">Small (Varmints, Prairie Dogs)</option>
                <option value="medium">Medium (Deer, Antelope, etc.)</option>
                <option value="large">Large (Elk, Moose, etc.)</option>
                <option value="dangerous">Dangerous Game (Bear, etc.)</option>
              </select>
            </div>
          </div>

          {/* Bullet Selection */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Target size={16} className="card-title-icon" />
                Bullet
              </h3>
              <button
                onClick={() => goToTutorialStep(1)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Manufacturer</label>
              <select
                value={formData.bulletCompany}
                onChange={(e) => handleInputChange('bulletCompany', e.target.value)}
                className="form-select"
              >
                <option value="">Select manufacturer</option>
                {Object.keys(bulletDatabase).map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>

            <div className="card-form-grid">
              {formData.bulletCompany && (
                <div className="form-group">
                  <label className="form-label">Caliber</label>
                  <select
                    value={formData.bulletCaliber}
                    onChange={(e) => handleInputChange('bulletCaliber', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select caliber</option>
                    {Object.keys(bulletDatabase[formData.bulletCompany] || {}).map(caliber => (
                      <option key={caliber} value={caliber}>{caliber}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.bulletCompany && formData.bulletCaliber && (
                <div className="form-group">
                  <label className="form-label">Bullet</label>
                  <select
                    value={formData.bulletType}
                    onChange={(e) => {
                      const bullet = bulletDatabase[formData.bulletCompany][formData.bulletCaliber]
                        .find(b => b.name === e.target.value);
                      if (bullet) {
                        handleBulletSelection(formData.bulletCompany, formData.bulletCaliber, bullet);
                      }
                    }}
                    className="form-select"
                  >
                    <option value="">Select bullet</option>
                    {(bulletDatabase[formData.bulletCompany][formData.bulletCaliber] || []).map(bullet => (
                      <option key={bullet.name} value={bullet.name}>{bullet.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Cartridge / Load Description</label>
              <input
                type="text"
                value={formData.bulletDescription}
                onChange={(e) => handleInputChange('bulletDescription', e.target.value)}
                placeholder="ex. 300 Win Mag"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Weight ({unitLabels.bulletWeight})</label>
              <input
                type="number"
                value={displayValue(formData.bulletWeight, 'bulletWeight')}
                onChange={(e) => handleInputChange('bulletWeight', parseInput(e.target.value, 'bulletWeight'))}
                className="form-input"
              />
            </div>
          </div>

          {/* Environment */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Thermometer size={16} className="card-title-icon" />
                Environment
              </h3>
              <button
                onClick={() => goToTutorialStep(3)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="card-form-grid">
              <div className="form-group">
                <label className="form-label">Elevation ({unitLabels.elevation})</label>
                <input
                  type="number"
                  value={displayValue(formData.elevation, 'elevation')}
                  onChange={(e) => handleElevationChange(parseInput(e.target.value, 'elevation'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Temperature ({unitLabels.temp})</label>
                <input
                  type="number"
                  value={displayValue(formData.temperature, 'temperature')}
                  onChange={(e) => handleInputChange('temperature', parseInput(e.target.value, 'temperature'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Pressure ({unitLabels.pressure})</label>
                <input
                  type="number"
                  step="0.01"
                  value={displayValue(formData.pressure, 'pressure')}
                  onChange={(e) => handleInputChange('pressure', parseInput(e.target.value, 'pressure'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Humidity (%)</label>
                <input
                  type="number"
                  value={formData.humidity}
                  onChange={(e) => handleInputChange('humidity', Number(e.target.value))}
                  className="form-input"
                />
              </div>

              <div className="form-group form-group-wide">
                <label className="form-label">
                  <span className="label-with-icon">
                    <Wind size={16} />
                    Wind Speed ({unitLabels.windSpeed})
                  </span>
                </label>
                <input
                  type="number"
                  value={displayValue(formData.windSpeed, 'windSpeed')}
                  onChange={(e) => handleInputChange('windSpeed', parseInput(e.target.value, 'windSpeed'))}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Sighting Data */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Gauge size={16} className="card-title-icon" />
                Scope &amp; Zero
              </h3>
              <button
                onClick={() => goToTutorialStep(4)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="card-form-grid">
              <div className="form-group">
                <label className="form-label">Scope Height ({unitLabels.scopeHeight})</label>
                <input
                  type="number"
                  step="0.1"
                  value={displayValue(formData.scopeHeight, 'scopeHeight')}
                  onChange={(e) => handleInputChange('scopeHeight', parseInput(e.target.value, 'scopeHeight'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Zero Range ({unitLabels.range})</label>
                <input
                  type="number"
                  value={displayValue(formData.zeroRange, 'zeroRange')}
                  onChange={(e) => handleInputChange('zeroRange', parseInput(e.target.value, 'zeroRange'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Zero Adjustment ({unitLabels.zeroAdjustment})</label>
                <input
                  type="number"
                  step="0.1"
                  value={displayValue(formData.zeroAdjustment, 'zeroAdjustment')}
                  onChange={(e) => handleInputChange('zeroAdjustment', parseInput(e.target.value, 'zeroAdjustment'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Click Value (MOA/click)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.clickValue}
                  onChange={(e) => handleInputChange('clickValue', Number(e.target.value))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Inclination (deg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.inclination}
                  onChange={(e) => handleInputChange('inclination', Number(e.target.value))}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Ballistic Data */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Calculator size={16} className="card-title-icon" />
                Ballistics
              </h3>
              <button
                onClick={() => goToTutorialStep(4)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="form-group">
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bcMode"
                    checked={formData.useBC}
                    onChange={() => handleInputChange('useBC', true)}
                    className="radio-input"
                  />
                  Use BC
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bcMode"
                    checked={!formData.useBC}
                    onChange={() => handleInputChange('useBC', false)}
                    className="radio-input"
                  />
                  Use Velocity
                </label>
              </div>
            </div>

            <div className="card-form-grid">
              <div className="form-group">
                <label className="form-label">Ballistic Coefficient</label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.ballisticCoefficient}
                  onChange={(e) => handleInputChange('ballisticCoefficient', Number(e.target.value))}
                  disabled={!formData.useBC}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Muzzle Velocity ({unitLabels.velocity})</label>
                <input
                  type="number"
                  value={displayValue(formData.muzzleVelocity, 'muzzleVelocity')}
                  onChange={(e) => handleInputChange('muzzleVelocity', parseInput(e.target.value, 'muzzleVelocity'))}
                  disabled={formData.useBC}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Range Card Settings */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Play size={16} className="card-title-icon" />
                Trajectory Range
              </h3>
              <button
                onClick={() => goToTutorialStep(4)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="card-form-grid card-form-grid-3">
              <div className="form-group">
                <label className="form-label">Start ({unitLabels.range})</label>
                <input
                  type="number"
                  value={displayValue(formData.startRange, 'startRange')}
                  onChange={(e) => handleInputChange('startRange', parseInput(e.target.value, 'startRange'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">End ({unitLabels.range})</label>
                <input
                  type="number"
                  value={displayValue(formData.endRange, 'endRange')}
                  onChange={(e) => handleInputChange('endRange', parseInput(e.target.value, 'endRange'))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Step ({unitLabels.range})</label>
                <input
                  type="number"
                  value={displayValue(formData.rangeIncrement, 'rangeIncrement')}
                  onChange={(e) => handleInputChange('rangeIncrement', parseInput(e.target.value, 'rangeIncrement'))}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Alternate Conditions */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <Gauge size={16} className="card-title-icon" />
                Alternate Conditions
              </h3>
              <button
                onClick={() => goToTutorialStep(4)}
                className="btn-help"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            {alternateColumns.map((col, idx) => (
              <div className="alt-col-block" key={idx}>
                <div className="form-group">
                  <label className="form-label">Column {idx + 1}</label>
                  <select
                    value={col.type}
                    onChange={(e) => updateAlternateColumn(idx, 'type', e.target.value)}
                    className="form-select"
                  >
                    <option value="None">None</option>
                    <option value="Incline">Incline</option>
                    <option value="Air Density">Air Density</option>
                  </select>
                </div>

                {col.type === 'Incline' && (
                  <div className="form-group">
                    <label className="form-label">Incline (deg)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={col.incline}
                      onChange={(e) => updateAlternateColumn(idx, 'incline', Number(e.target.value))}
                      className="form-input"
                    />
                  </div>
                )}

                {col.type === 'Air Density' && (
                  <>
                    <div className="card-form-grid">
                      <div className="form-group">
                        <label className="form-label">Elevation (ft)</label>
                        <input
                          type="number"
                          value={col.elevation}
                          onChange={(e) => updateAlternateColumn(idx, 'elevation', Number(e.target.value))}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Temperature (°F)</label>
                        <input
                          type="number"
                          value={col.temperature}
                          onChange={(e) => updateAlternateColumn(idx, 'temperature', Number(e.target.value))}
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pressure (in Hg)</label>
                      <input
                        type="number"
                        value={pressureFromAltitude(Number(col.elevation) || 0).toFixed(2)}
                        disabled
                        className="form-input"
                      />
                    </div>
                  </>
                )}

                {col.type !== 'None' && (
                  <label className="alt-col-variation">
                    <input
                      type="checkbox"
                      checked={col.showVariation}
                      onChange={(e) => updateAlternateColumn(idx, 'showVariation', e.target.checked)}
                    />
                    <span>Show Variation</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Field Data Shooting Points Section */}
        {showFieldData && (
          <div className="field-data-container">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <Target size={16} className="card-title-icon" />
                  Field Data
                  {fieldDataPoints.some(p => Number(p.target) > 0) && (
                    <span className="field-data-count">{fieldDataPoints.filter(p => Number(p.target) > 0).length}</span>
                  )}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setFieldDataCollapsed(c => !c)}
                    className="btn-help"
                    aria-label={fieldDataCollapsed ? 'Expand' : 'Collapse'}
                  >
                    {fieldDataCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} style={{ transform: 'rotate(-90deg)' }} />}
                  </button>
                  <button
                    onClick={() => goToTutorialStep(2)}
                    className="btn-help"
                  >
                    <HelpCircle size={16} />
                  </button>
                </div>
              </div>

              {!fieldDataCollapsed && (
                <div className="field-data-input">
                  {fieldDataPoints.map((point, idx) => (
                    <div className="field-data-row" key={point.id}>
                      <div className="form-group">
                        <label className="form-label">Target ({unitLabels.range})</label>
                        <input
                          type="number"
                          value={point.target}
                          onChange={(e) => updateFieldDataPoint(point.id, 'target', e.target.value)}
                          className="form-input"
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Clicks</label>
                        <input
                          type="number"
                          value={point.clicks}
                          onChange={(e) => updateFieldDataPoint(point.id, 'clicks', e.target.value)}
                          className="form-input"
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Adjustment ({unitLabels.drop})</label>
                        <input
                          type="number"
                          step="0.1"
                          value={point.adjustment}
                          onChange={(e) => updateFieldDataPoint(point.id, 'adjustment', e.target.value)}
                          className="form-input"
                          placeholder="0"
                        />
                      </div>
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => removeFieldDataPoint(point.id)}
                          className="btn-remove field-data-remove"
                          aria-label="Remove data point"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addFieldDataPoint}
                    className="btn-add-field-point"
                  >
                    <Plus size={16} />
                    Add Another Field Shooting Data Point
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <div className="results-container">
            <div className="card-header">
              <h3 className="results-title">Ballistic Results</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '0.375rem', overflow: 'hidden' }}>
                  {['imperial', 'metric'].map(sys => (
                    <button
                      key={sys}
                      type="button"
                      onClick={() => handleInputChange('unitSystem', sys)}
                      style={{
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.85rem',
                        border: 'none',
                        cursor: 'pointer',
                        background: formData.unitSystem === sys ? '#111827' : '#fff',
                        color: formData.unitSystem === sys ? '#fff' : '#111827',
                        fontWeight: formData.unitSystem === sys ? 600 : 400
                      }}
                    >
                      {sys === 'imperial' ? 'Imperial' : 'Metric'}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => goToTutorialStep(4)}
                  className="btn-help"
                >
                  <HelpCircle size={16} />
                </button>
              </div>
            </div>

            <div className="results-grid">
              <div className="result-card">
                <h4 className="result-card-title">True BC</h4>
                <p className="result-card-value">{results.trueBc}</p>
              </div>
              <div className="result-card">
                <h4 className="result-card-title">True Muzzle Velocity</h4>
                <p className="result-card-value">{displayValue(results.trueMuzzleVelocity, 'muzzleVelocity')} {unitLabels.velocity}</p>
              </div>
              <div className="result-card">
                <h4 className="result-card-title">Field Data Points</h4>
                <p className="result-card-value">{fieldDataPoints.filter(p => Number(p.target) > 0).length}</p>
              </div>
            </div>

            {(() => {
              const angularLabel = 'MOA';
              const keThresholdsFtLb = { small: 500, medium: 1000, large: 1500, dangerous: 2000 };
              const keThresholdFtLb = keThresholdsFtLb[formData.gameSize] ?? null;
              const energyCellStyle = { color: '#dc2626', fontWeight: 600 };

              return (
                <div className="table-container">
                  <table className="results-table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-cell">Range ({unitLabels.range})</th>
                        <th className="table-cell">Velocity ({unitLabels.velocity})</th>
                        <th className="table-cell">Energy ({unitLabels.energy})</th>
                        <th className="table-cell">Path ({angularLabel})</th>
                        <th className="table-cell">Path (clicks)</th>
                        <th className="table-cell">Path ({unitLabels.drop})</th>
                        <th className="table-cell">Deflection ({angularLabel})</th>
                        <th className="table-cell">Deflection (clicks)</th>
                        <th className="table-cell">Deflection ({unitLabels.windDrift})</th>
                        {(results.alternates || []).map((alt, i) => alt && (
                          <th key={i} className="table-cell">
                            {alt.type}{alt.showVariation ? ' var.' : ''} (Clks)
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.trajectoryData.map((row, index) => {
                        const belowKE = keThresholdFtLb !== null && Number(row.energy) < keThresholdFtLb;
                        const primaryClicks = row.dropClicks;
                        return (
                          <tr key={index} className="table-row">
                            <td className="table-cell table-cell-range">{displayValue(row.range, 'startRange')}</td>
                            <td className="table-cell">{displayValue(row.velocity, 'velocity')}</td>
                            <td className="table-cell" style={belowKE ? energyCellStyle : undefined}>
                              {displayValue(row.energy, 'energy')}
                            </td>
                            <td className="table-cell">{row.dropMOA}</td>
                            <td className="table-cell">{row.dropClicks}</td>
                            <td className="table-cell">{displayValue(row.drop, 'drop')}</td>
                            <td className="table-cell">{row.windMOA}</td>
                            <td className="table-cell">{row.windClicks}</td>
                            <td className="table-cell">{displayValue(row.windDrift, 'windDrift')}</td>
                            {(results.alternates || []).map((alt, i) => {
                              if (!alt) return null;
                              const altClk = alt.clicks[index];
                              const display = alt.showVariation
                                ? +(altClk - primaryClicks).toFixed(1)
                                : altClk;
                              return (
                                <td
                                  key={i}
                                  className="table-cell"
                                  style={alt.showVariation ? { color: '#1d4ed8' } : undefined}
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {keThresholdFtLb !== null && (
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      Energy values in <span style={energyCellStyle}>red</span> are below the recommended {keThresholdFtLb} ft-lb minimum for {formData.gameSize} game.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Tutorial Modal */}
        {showTutorial && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-header-info">
                  <div className="modal-icon">
                    <Play size={20} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <h3 className="modal-title">Tutorial</h3>
                    <p className="modal-subtitle">
                      Step {tutorialStep + 1} of {tutorialSteps.length}
                    </p>
                  </div>
                </div>
                <button onClick={closeTutorial} className="modal-close">
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                <h4 className="modal-content-title">
                  {tutorialSteps[tutorialStep].title}
                </h4>
                <p className="modal-content-text">
                  {tutorialSteps[tutorialStep].content}
                </p>

                {/* Tutorial-specific content */}
                {tutorialStep === 1 && (
                  <div className="tutorial-info-box">
                    <h5 className="tutorial-info-title">Comprehensive Database Includes:</h5>
                    <ul className="tutorial-info-list">
                      <li><strong>Hornady:</strong> 80+ bullets including ELD Match, ELD-X, V-MAX, SST</li>
                      <li><strong>Sierra:</strong> MatchKing, GameKing, BlitzKing series</li>
                      <li><strong>Berger:</strong> VLD Target, VLD Hunting, Hybrid series</li>
                      <li><strong>Nosler:</strong> AccuBond, Ballistic Tip, Partition series</li>
                      <li><strong>Barnes:</strong> TSX, TTSX, LRX series</li>
                    </ul>
                  </div>
                )}

                {tutorialStep === 2 && (
                  <div className="tutorial-info-box">
                    <h5 className="tutorial-info-title">Field Data Features:</h5>
                    <ul className="tutorial-info-list">
                      <li>• Record actual shooting results at various ranges</li>
                      <li>• Track environmental conditions for each shot</li>
                      <li>• Compare calculated vs. actual performance</li>
                      <li>• Build a database of your rifle's performance</li>
                      <li>• Validate ballistic coefficient accuracy</li>
                    </ul>
                  </div>
                )}

                <div className="progress-container">
                  <div className="progress-header">
                    <span>Progress</span>
                    <span>{Math.round(((tutorialStep + 1) / tutorialSteps.length) * 100)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${((tutorialStep + 1) / tutorialSteps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  onClick={prevTutorialStep}
                  disabled={tutorialStep === 0}
                  className="btn btn-secondary"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="progress-dots">
                  {tutorialSteps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setTutorialStep(index)}
                      className={`progress-dot ${index === tutorialStep
                          ? 'active'
                          : index < tutorialStep
                            ? 'completed'
                            : 'inactive'
                        }`}
                    />
                  ))}
                </div>

                <button onClick={nextTutorialStep} className="btn btn-primary">
                  {tutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                  {tutorialStep !== tutorialSteps.length - 1 && <ChevronRight size={16} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Modal */}
        {isCalculating && (
          <div className="modal-overlay">
            <div className="loading-modal">
              <div className="loading-spinner"></div>
              <h3 className="loading-title">Calculating...</h3>
              <p className="loading-text">Processing ballistic data</p>
            </div>
          </div>
        )}

        <footer className="footer">
          <div className="footer-left">
            <span className="footer-product">TrueBC<sup>®</sup> Calculator</span>
            <span className="footer-sep" aria-hidden="true">·</span>
            <span>© {new Date().getFullYear()} Huskemaw Optics</span>
          </div>
          <div className="footer-right">
            Built by{' '}
            <a
              href="https://coastaldivision.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              Coastal Division
            </a>
          </div>
        </footer>
      </div>

      <style>{`
        .ballistics-calculator {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          font-family: system-ui, -apple-system, sans-serif;
          color: #1e293b;
          padding: 20px;
          border-radius: 12px;
        }

        .calculator-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .header {
          margin-bottom: 12px;
          background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
          border-radius: 8px;
          padding: 10px 20px;
          color: white;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1200px;
          margin: 0 auto;
          gap: 20px;
        }

        .header-left {
          flex-shrink: 0;
        }

        .header-right {
          display: flex;
          align-items: center;
          flex: 1;
          justify-content: flex-end;
        }

        .header-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn-header {
          background-color: rgba(255, 255, 255, 0.15);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 6px 14px;
          font-size: 13px;
          backdrop-filter: blur(4px);
          white-space: nowrap;
        }

        .btn-header:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .title {
          font-size: 1.75rem;
          font-weight: bold;
          color: white;
          margin: 0 0 2px 0;
          line-height: 1.1;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.875rem;
          margin: 0;
          font-weight: 400;
        }

        .tagline {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
          margin: 0;
          line-height: 1.4;
          font-style: italic;
        }


        .sticky-action-bar {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(248, 250, 252, 0.95);
          backdrop-filter: blur(8px);
          border-bottom: 1px solid #e2e8f0;
          margin: 0 -20px 16px -20px;
          padding: 8px 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .sticky-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: center;
          max-width: 1400px;
          margin: 0 auto;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          text-decoration: none;
          white-space: nowrap;
        }

        .btn-large {
          padding: 10px 20px;
          font-size: 15px;
          font-weight: 700;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .btn-primary {
          background-color: #1d4ed8;
          color: white;
        }

        .btn-secondary {
          background-color: #64748b;
          color: white;
        }

        .btn-success {
          background-color: #059669;
          color: white;
        }

        .btn-info {
          background-color: #0891b2;
          color: white;
        }

        .btn-turret {
          background-color: #dc2626;
          color: white;
        }

        .btn-turret:hover:not(:disabled) {
          background-color: #b91c1c;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-help {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: color 0.2s ease;
        }

        .btn-help:hover {
          color: #2563eb;
        }

        .btn-remove {
          background: #dc2626;
          color: white;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .btn-remove:hover {
          background: #b91c1c;
        }

        .grid-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 14px;
          margin: 0 auto 20px auto;
          max-width: 1200px;
          align-items: start;
        }

        .card {
          background-color: white;
          border-radius: 12px;
          padding: 16px 18px;
          border: 1px solid #dbeafe;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 0 0 14px 0;
          padding-bottom: 10px;
          border-bottom: 1px solid #eef2ff;
        }

        .card-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 14px;
        }

        .card-form-grid-3 {
          grid-template-columns: repeat(3, 1fr);
        }

        .card-form-grid .form-group,
        .card-form-grid-3 .form-group {
          margin-bottom: 0;
        }

        .card-form-grid .form-group-wide {
          grid-column: 1 / -1;
        }

        .card > .form-group:last-child,
        .card > .card-form-grid:last-child {
          margin-bottom: 0;
        }

        .alt-col-block {
          padding: 10px 0;
          border-bottom: 1px dashed #e2e8f0;
        }

        .alt-col-block:last-of-type {
          border-bottom: none;
          padding-bottom: 0;
        }

        .alt-col-block:first-of-type {
          padding-top: 0;
        }

        .alt-col-variation {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #374151;
          cursor: pointer;
          margin-top: 4px;
        }

        .alt-col-variation input {
          accent-color: #1d4ed8;
        }

        .field-data-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 7px;
          border-radius: 11px;
          background: #1d4ed8;
          color: white;
          font-size: 12px;
          font-weight: 700;
          margin-left: 8px;
        }

        .card-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #1e3a8a;
          margin: 0;
        }

        .card-title-icon {
          color: #2563eb;
          flex-shrink: 0;
        }

        .form-group {
          margin-bottom: 10px;
          min-width: 0;
        }

        .form-group-wide {
          grid-column: span 2;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 4px;
          letter-spacing: 0.01em;
        }

        .form-input,
        .form-select {
          box-sizing: border-box;
          width: 100%;
          min-width: 0;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          background-color: white;
          color: #1f2937;
          transition: border-color 0.2s ease;
        }

        .form-input:focus,
        .form-select:focus {
          outline: 2px solid #1d4ed8;
          border-color: #1d4ed8;
        }

        .form-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .radio-group {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .radio-input {
          accent-color: #1d4ed8;
        }

        .label-with-icon {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .toggle-container {
          display: flex;
          align-items: center;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }

        .header .toggle-label {
          color: white;
        }

        .toggle-text {
          user-select: none;
          white-space: nowrap;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }

        .toggle-input {
          opacity: 0;
          width: 0;
          height: 0;
          position: absolute;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          transition: 0.3s ease;
          border-radius: 24px;
          border: 2px solid transparent;
        }

        .header .toggle-slider {
          background-color: rgba(255, 255, 255, 0.3);
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: 0.3s ease;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-input:checked + .toggle-slider {
          background-color: #1d4ed8;
        }

        .header .toggle-input:checked + .toggle-slider {
          background-color: rgba(255, 255, 255, 0.9);
        }

        .header .toggle-input:checked + .toggle-slider:before {
          background-color: #1d4ed8;
        }

        .toggle-input:focus + .toggle-slider {
          outline: 2px solid #1d4ed8;
          outline-offset: 2px;
        }

        .toggle-input:checked + .toggle-slider:before {
          transform: translateX(20px);
        }

        .toggle-slider:hover {
          background-color: #94a3b8;
        }

        .header .toggle-slider:hover {
          background-color: rgba(255, 255, 255, 0.4);
        }

        .toggle-input:checked + .toggle-slider:hover {
          background-color: #1e40af;
        }

        .header .toggle-input:checked + .toggle-slider:hover {
          background-color: rgba(255, 255, 255, 1);
        }

        .field-data-container {
          max-width: 1200px;
          margin: 0 auto 30px auto;
        }

        .field-data-input {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .field-data-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 12px;
          align-items: end;
          padding-bottom: 10px;
          border-bottom: 1px solid #eef2ff;
        }

        .field-data-row:last-of-type {
          border-bottom: none;
          padding-bottom: 0;
        }

        .field-data-remove {
          align-self: end;
          margin-bottom: 2px;
        }

        .btn-add-field-point {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          margin-top: 8px;
          background: #1f2937;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .btn-add-field-point:hover {
          background: #111827;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          align-items: end;
        }

        .field-data-list {
          margin-top: 24px;
        }

        .field-data-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 16px 0;
        }

        .results-container {
          background-color: white;
          border-radius: 12px;
          padding: 24px;
          border: 1px solid #dbeafe;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          max-width: 1200px;
          margin: 0 auto;
        }

        .results-title {
          font-size: 1.5rem;
          font-weight: bold;
          color: #1e3a8a;
          margin: 0;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .result-card {
          background-color: #f8fafc;
          border-radius: 8px;
          padding: 16px;
        }

        .result-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px 0;
        }

        .result-card-value {
          font-size: 1.5rem;
          font-weight: bold;
          color: #1d4ed8;
          margin: 0;
        }

        .table-container {
          overflow-x: auto;
        }

        .results-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .table-header {
          background-color: #f1f5f9;
          font-weight: 600;
        }

        .table-cell {
          padding: 8px 12px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
        }

        .table-cell-range {
          font-weight: 600;
        }

        .table-row {
          transition: background-color 0.2s ease;
        }

        .table-row:nth-child(even) {
          background-color: #f8fafc;
        }

        .table-row:hover {
          background-color: #e2e8f0 !important;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background-color: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
        }

        .modal-header-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .modal-icon {
          background-color: #1d4ed8;
          border-radius: 50%;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0;
        }

        .modal-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          color: #1e3a8a;
          background-color: #f1f5f9;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .modal-content-title {
          font-size: 1.4rem;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 16px 0;
        }

        .modal-content-text {
          color: #374151;
          line-height: 1.6;
          font-size: 15px;
          margin: 0 0 20px 0;
        }

        .tutorial-info-box {
          background-color: #f1f5f9;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .tutorial-info-title {
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 8px 0;
          font-size: 15px;
        }

        .tutorial-info-list {
          color: #374151;
          font-size: 14px;
          margin: 0;
          padding-left: 18px;
        }

        .tutorial-info-list li {
          margin-bottom: 4px;
          line-height: 1.4;
        }

        .progress-container {
          margin-bottom: 20px;
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          color: #64748b;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .progress-bar {
          width: 100%;
          height: 6px;
          background-color: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background-color: #1d4ed8;
          transition: width 0.3s ease;
        }

        .modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-top: 1px solid #e2e8f0;
          gap: 12px;
        }

        .progress-dots {
          display: flex;
          gap: 6px;
          justify-content: center;
          align-items: center;
          flex: 1;
          max-width: 180px;
          margin: 0 12px;
        }

        .progress-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .progress-dot:hover {
          transform: scale(1.3);
        }

        .progress-dot.active {
          background-color: #1d4ed8;
          box-shadow: 0 0 0 2px rgba(29, 78, 216, 0.3);
        }

        .progress-dot.completed {
          background-color: #93c5fd;
        }

        .progress-dot.inactive {
          background-color: #d1d5db;
        }

        .loading-modal {
          background-color: white;
          border-radius: 12px;
          padding: 32px;
          text-align: center;
          max-width: 300px;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #1d4ed8;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        .loading-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 8px;
        }

        .loading-text {
          color: #64748b;
          margin: 0;
        }

        .footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          max-width: 1200px;
          margin: 40px auto 0;
          padding: 16px 4px;
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          font-size: 13px;
        }

        .footer-left,
        .footer-right {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .footer-product {
          font-weight: 600;
          color: #1e3a8a;
        }

        .footer-sep {
          color: #cbd5e1;
        }

        .footer-link {
          color: #1d4ed8;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s ease;
        }

        .footer-link:hover {
          color: #1e40af;
          text-decoration: underline;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Print Styles for Range Card */
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          body {
            background: white !important;
            color: black !important;
            font-family: Arial, sans-serif !important;
            margin: 0;
            padding: 0;
          }

          .ballistics-calculator {
            background: white !important;
            color: black !important;
            padding: 20px !important;
          }

          /* Hide everything except results when printing */
          .header,
          .button-container,
          .sticky-action-bar,
          .grid-container,
          .field-data-container,
          .footer,
          .modal-overlay,
          .btn-help,
          .tutorial-info-box {
            display: none !important;
          }

          .results-container {
            display: block !important;
            background: white !important;
            border: 2px solid black !important;
            padding: 20px !important;
            margin: 0 !important;
            max-width: none !important;
            box-shadow: none !important;
            page-break-inside: avoid;
          }

          .results-container::before {
            content: "Range Card / Data Collection Sheet";
            display: block;
            font-size: 20px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid black;
          }

          .card-header {
            display: none !important;
          }

          .results-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 20px !important;
            margin-bottom: 20px !important;
            padding: 15px !important;
            border: 1px solid black !important;
            background: #f9f9f9 !important;
          }

          .results-grid::before {
            content: "Bullet & Environmental Data";
            grid-column: 1 / -1;
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 10px;
            text-align: center;
            border-bottom: 1px solid black;
            padding-bottom: 5px;
          }

          .result-card {
            background: white !important;
            border: 1px solid #ccc !important;
            padding: 10px !important;
            margin: 0 !important;
          }

          .result-card-title {
            font-size: 12px !important;
            color: black !important;
            margin: 0 0 5px 0 !important;
            font-weight: bold !important;
          }

          .result-card-value {
            font-size: 14px !important;
            color: black !important;
            margin: 0 !important;
            font-weight: bold !important;
          }

          /* Add bullet info to print */
          .results-grid::after {
            content: "Bullet: ${formData.bulletCompany} ${formData.bulletCaliber} ${formData.bulletType} | Weight: ${formData.bulletWeight}gr | BC: ${formData.ballisticCoefficient}";
            grid-column: 1 / -1;
            font-size: 12px;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            text-align: center;
          }

          .table-container {
            overflow: visible !important;
            margin-top: 20px !important;
          }

          .results-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 11px !important;
            border: 2px solid black !important;
          }

          .table-header {
            background: #e0e0e0 !important;
            font-weight: bold !important;
          }

          .table-header th {
            background: #e0e0e0 !important;
            color: black !important;
          }

          .table-cell {
            border: 1px solid black !important;
            padding: 4px 6px !important;
            text-align: center !important;
            font-size: 11px !important;
            color: black !important;
          }

          .table-cell-range {
            font-weight: bold !important;
            background: #f5f5f5 !important;
          }

          .table-row:nth-child(even) {
            background: #f9f9f9 !important;
          }

          .table-row:nth-child(odd) {
            background: white !important;
          }

          /* Add field data collection section */
          .results-container::after {
            content: "Field Data Collection | Velocity: _____________ | Altitude: _____________ | Temperature: _____________ | Pressure: _____________ | Humidity: _____________ | Mid Target Range: _____________ | Clicks: _____________ | Inches: _____________ | Far Target Range: _____________ | Clicks: _____________ | Inches: _____________";
            display: block;
            margin-top: 20px;
            padding: 15px;
            border: 1px solid black;
            background: #f9f9f9;
            font-size: 12px;
            line-height: 1.8;
            white-space: pre-line;
          }

          /* Copyright notice */
          .results-container::before {
            content: "Enhanced Ballistics Calculator - Range Card" "Generated: ${new Date().toLocaleDateString()}";
            white-space: pre-line;
          }

          /* Page break control */
          @page {
            margin: 0.5in;
            size: letter;
          }
        }

        @media (max-width: 768px) {
          .ballistics-calculator {
            padding: 15px;
          }

          .header {
            padding: 12px 16px;
            margin-bottom: 15px;
          }

          .header-content {
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 10px;
          }

          .header-left {
            order: 1;
            text-align: center;
          }

          .header-right {
            order: 2;
          }

          .header-controls {
            flex-direction: row;
            justify-content: center;
            width: 100%;
            gap: 10px;
            flex-wrap: wrap;
          }

          .title {
            font-size: 1.5rem;
          }

          .subtitle {
            font-size: 0.875rem;
          }

          .tagline {
            font-size: 0.9rem;
          }
          
          .grid-container {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .card-form-grid,
          .card-form-grid-3 {
            grid-template-columns: 1fr;
          }

          .form-group-wide {
            grid-column: span 1;
          }

          .sticky-action-bar {
            margin: 0 -15px 15px -15px;
            padding: 10px 15px;
          }

          .sticky-actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .btn {
            flex: 0 0 auto;
            width: 100%;
            padding: 10px 12px;
            font-size: 13px;
            white-space: normal;
            justify-content: center;
          }

          .btn-large {
            grid-column: 1 / -1;
            width: 100%;
            padding: 12px 16px;
            font-size: 15px;
            order: -1;
          }

          .card {
            padding: 16px;
          }

          .form-input,
          .form-select {
            font-size: 16px;
            padding: 10px 12px;
          }

          .form-select {
            padding-right: 40px;
          }

          .radio-group {
            flex-direction: column;
            gap: 8px;
          }

          .radio-label {
            font-size: 16px;
            padding: 8px 0;
          }

          .table-container {
            font-size: 12px;
          }

          .table-cell {
            padding: 6px 4px;
            font-size: 11px;
          }
          
          .modal-content {
            margin: 20px;
            max-height: 90vh;
          }
        }

        @media (max-width: 480px) {
          .ballistics-calculator {
            padding: 10px;
          }
          
          .title {
            font-size: 1.75rem;
          }

          .card {
            padding: 12px;
          }

          .card-title {
            font-size: 1.1rem;
          }

          .form-input,
          .form-select {
            font-size: 16px;
            padding: 12px 14px;
          }

          .form-select {
            padding-right: 45px;
          }

          .table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .results-table {
            min-width: 600px;
          }

          .table-cell {
            padding: 8px 6px;
            font-size: 12px;
            white-space: nowrap;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .field-data-title {
            font-size: 1rem;
          }

          .footer {
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-top: 32px;
            padding: 16px 12px;
            font-size: 12px;
            gap: 6px;
          }
        }
      `}</style>
    </div>
  );
};

export default BallisticsCalculator;
